/**
 * AkteDetail — Einzelne Akte mit Prozess-Unterseiten
 * Routing: /akten/:betrieb/:aktenNr/:section
 *
 * DESIGN: Nordic Command Center — Clean, professionell
 * NEU (Autoglas): Scheibe-Section mit Grüne-Plakette-Feld
 * NEU (Autoglas): Montage-Section mit Plaketten-Status-Anzeige
 */

import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAkteByNr, useSvAktenDaten, useTextbausteine } from '@/hooks/useSupabase';
import { STATUS_LABELS, STATUS_COLORS, type BusinessUnit } from '@/lib/supabase';
import { BETRIEBE, getBetriebColor, type BetriebId } from '@/lib/betriebe';
import { istSvVerfahren } from '@/lib/navigation';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import type { Akte } from '@/lib/database.types';
import {
  stellungnahmeStatusLabel,
  berechneSchriftsatzfristBadge,
  schriftsatzfristBadgeText,
} from '@/lib/akten-stellungnahme';
import StellungnahmeTab from '@/components/akte/StellungnahmeTab';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import {
  FileText, Loader2, Printer, Pencil, MoreHorizontal,
  ChevronRight, Home, Layers, Wrench, CheckCircle2,
  AlertTriangle, Info, Shield, Car, Clock, Building2, X,
  Instagram, Share2, Scale, Send, TrendingDown, DollarSign,
  Phone, Mail, MapPin, ExternalLink, Copy, Plus, Gavel,
  Users, RefreshCw, Link2, Camera, Timer, Euro, ChevronDown,
} from 'lucide-react';
import { berechneMietdauerTage, berechneRestschuld } from '@/lib/mietwagen-restschuld';
import { supabase } from '@/lib/supabase';
import { Sentry } from '@/lib/sentry';
import { toast } from 'sonner';
import GutachtenEditor from '@/components/GutachtenEditor';
import DokumentenZusammenfuehrung from '@/components/DokumentenZusammenfuehrung';
import DokumentGenerator from '@/components/DokumentGenerator';
import AktenOrdner from '@/components/AktenOrdner';
import MietwagenDokumente from '@/components/MietwagenDokumente';
import MietwagenAnfrageSection from '@/components/MietwagenAnfrageSection';
import MietwagenRechnungGenerator from '@/components/MietwagenRechnungGenerator';
import MietwagenVertragWizard from '@/components/MietwagenVertragWizard';
import MietwagenRueckgabe from '@/components/MietwagenRueckgabe';
import MietwagenRueckgabeProtokoll from '@/components/fahrzeuge/MietwagenRueckgabeProtokoll';
import MietwagenEditableSection from '@/components/MietwagenEditableSection';
import type { FieldConfig } from '@/components/MietwagenEditableSection';
import MietwagenKorrespondenz from '@/components/MietwagenKorrespondenz';
import MietwagenStatusBadge from '@/components/MietwagenStatusBadge';
import { Badge } from '@/components/ui/badge';
import MietwagenWorkflowStepper from '@/components/MietwagenWorkflowStepper';
import {
  AuftragSection,
  FahrzeugSection,
  BesichtigungenSection,
  LichtbilderSection,
  BewertungSection,
  RestwertSection,
  MinderwertSection,
  RechnungSection,
  ProtokollSection,
  DownloadFromWinValueButton,
  WinValueDokumenteList,
} from '@/components/akte/AkteSections';
import LinkedAkteBanner from '@/components/akte/LinkedAkteBanner';
import BeteiligtenTab from '@/components/akte/BeteiligtenTab';
import DokumenteSection from '@/components/akte/DokumenteSection';
import WerkstattCrossLinkCard from '@/components/akte/WerkstattCrossLinkCard';
import WerkstattAnlegenModal from '@/components/akte/WerkstattAnlegenModal';
import MietwagenCrossLinkCard from '@/components/akte/MietwagenCrossLinkCard';
import GutachtenCrossLinkCard from '@/components/akte/GutachtenCrossLinkCard';
import WerkstattCrossLinkBadge from '@/components/akte/WerkstattCrossLinkBadge';
import WinValueMietwagenImportCard from '@/components/akte/WinValueMietwagenImportCard';
import AkteAufgabenCard from '@/components/akte/AkteAufgabenCard';
import MietgoCrossLinkBadge from '@/components/akte/MietgoCrossLinkBadge';
import StickyAktenkopfzeile from '@/components/akte/StickyAktenkopfzeile';
import { ErstellerBadge } from '@/components/akte/ErstellerBadge';
import { STELLUNGNAHME_LABEL } from '@/components/akte/StellungnahmeLabel';
import LinkGeneratorDrawer from '@/components/LinkGeneratorDrawer';

const SECTION_LABELS: Record<string, string> = {
  uebersicht:    'Akte Übersicht',
  auftrag:       'Auftrag',
  beteiligte:    'Beteiligte',
  unterschrift:  'Unterschrift',
  fahrzeug:      'Fahrzeug',
  kundendaten:   'Kundendaten',
  besichtigungen:'Besichtigungen',
  begutachtung:  'Begutachtung',
  lichtbilder:   'Lichtbilder',
  instandsetzung:'Instandsetzung',
  bewertung:     'Bewertung',
  restwert:      'Restwert',
  minderwert:    'Minderwert',
  beurteilung:   'Beurteilung',
  gutachten:     'Gutachten',
  rechnung:      'Rechnung',
  forderung:     'Forderung & Kürzung',
  anwalt:        'Anwalt',
  dokumente:     'Dokumente',
  protokoll:     'Protokoll',
  // Mietwagen-spezifisch
  korrespondenz: 'Korrespondenz',
  // Autoglas-spezifisch
  scheibe:       'Scheibe & Material',
  terminplanung: 'Terminplanung',
  montage:       'Montage',
  qualitaet:     'Qualitätskontrolle',
  garantie:      'Garantie',
};

// ─── Grüne Plakette Types ─────────────────────────────────────

type PlaketteStatus = 'nein' | 'ja' | 'vorhanden';

const PLAKETTE_OPTIONS: { value: PlaketteStatus; label: string; desc: string; color: string; bg: string }[] = [
  { value: 'ja',        label: 'Ja, gewünscht',         desc: 'Grüne Plakette wird bei der Montage angebracht',         color: '#059669', bg: '#D1FAE5' },
  { value: 'nein',      label: 'Nein, nicht gewünscht', desc: 'Keine Plakette erforderlich',                            color: '#6B7280', bg: '#F3F4F6' },
  { value: 'vorhanden', label: 'Bereits vorhanden',     desc: 'Plakette ist bereits am Fahrzeug vorhanden',             color: '#1D4ED8', bg: '#DBEAFE' },
];

const PLAKETTE_STORAGE_KEY = 'autoglas_plakette_v1';

function loadPlaketteData(): Record<string, PlaketteStatus> {
  try {
    const raw = localStorage.getItem(PLAKETTE_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function savePlaketteData(data: Record<string, PlaketteStatus>) {
  try { localStorage.setItem(PLAKETTE_STORAGE_KEY, JSON.stringify(data)); } catch {}
}

// ─── Autoglas Scheibe Section ────────────────────────────────────

function AutoglasScheibeSection({ aktenNr }: { aktenNr: string }) {
  const [plaketteData, setPlaketteData] = useState<Record<string, PlaketteStatus>>(loadPlaketteData);
  const currentStatus = plaketteData[aktenNr] || null;

  const handleSelect = (val: PlaketteStatus) => {
    const updated = { ...plaketteData, [aktenNr]: val };
    setPlaketteData(updated);
    savePlaketteData(updated);
    const opt = PLAKETTE_OPTIONS.find(o => o.value === val);
    toast.success('Plaketten-Status gespeichert', { description: opt?.label });
  };

  return (
    <div className="space-y-5">
      {/* Scheibe & Material Karte */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
            <Layers className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-[15px] font-bold text-gray-900">Scheibe & Material</h3>
            <p className="text-[12px] text-gray-500">Scheibendaten und Zubehör für den Tausch</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Scheibentyp', placeholder: 'z.B. Frontscheibe, Heckscheibe, Seitenscheibe' },
            { label: 'OEM-Nummer', placeholder: 'z.B. 5G0845011' },
            { label: 'Hersteller', placeholder: 'z.B. Pilkington, Saint-Gobain' },
            { label: 'Bestellnummer', placeholder: 'Interne Bestellnummer' },
          ].map(f => (
            <div key={f.label}>
              <label className="block text-[12px] font-semibold text-gray-500 mb-1">{f.label}</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-gray-50"
                placeholder={f.placeholder}
                onClick={() => toast('Scheibendaten', { description: '○ Geplant — Dateneingabe wird gespeichert' })}
              />
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-[12px] text-amber-700">
            <span className="font-semibold">○ Geplant:</span> Scheibendaten werden in der nächsten Version mit der Bestellverwaltung verknüpft.
          </p>
        </div>
      </div>

      {/* ─── GRÜNE PLAKETTE ─── */}
      <div className="bg-white rounded-xl border-2 border-emerald-200 p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-[15px] font-bold text-gray-900">Grüne Plakette</h3>
            <p className="text-[12px] text-gray-500">Umweltplakette für das Fahrzeug</p>
          </div>
          {currentStatus && (
            <span
              className="ml-auto px-3 py-1 rounded-full text-[11px] font-bold"
              style={{
                background: PLAKETTE_OPTIONS.find(o => o.value === currentStatus)?.bg,
                color: PLAKETTE_OPTIONS.find(o => o.value === currentStatus)?.color,
              }}
            >
              {PLAKETTE_OPTIONS.find(o => o.value === currentStatus)?.label}
            </span>
          )}
        </div>

        <p className="text-[13px] text-gray-600 mb-4">
          Bitte wählen Sie den Plaketten-Status für diesen Auftrag. Diese Information wird im Montageschritt angezeigt.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PLAKETTE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                currentStatus === opt.value
                  ? 'shadow-md scale-[1.01]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              style={currentStatus === opt.value ? { borderColor: opt.color, background: opt.bg } : {}}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                  style={{ borderColor: opt.color }}
                >
                  {currentStatus === opt.value && (
                    <div className="w-2 h-2 rounded-full" style={{ background: opt.color }} />
                  )}
                </div>
                <span className="text-[13px] font-bold" style={{ color: opt.color }}>{opt.label}</span>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">{opt.desc}</p>
            </button>
          ))}
        </div>

        {currentStatus === 'ja' && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-start gap-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-[13px] text-emerald-800">
              <span className="font-semibold">Plakette wird angebracht.</span> Der Monteur wird beim Montageschritt daran erinnert, die grüne Plakette am Fahrzeug anzubringen.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Autoglas Montage Section ────────────────────────────────────

function AutoglasMontageSection({ aktenNr, kennzeichen }: { aktenNr: string; kennzeichen?: string }) {
  const [plaketteData] = useState<Record<string, PlaketteStatus>>(loadPlaketteData);
  const plaketteStatus = plaketteData[aktenNr] || null;
  const plaketteOpt = PLAKETTE_OPTIONS.find(o => o.value === plaketteStatus);

  const MONTAGE_SCHRITTE = [
    { id: 1, label: 'Fahrzeug vorbereiten', desc: 'Fahrzeug reinigen, alte Scheibe entfernen' },
    { id: 2, label: 'Dichtung prüfen', desc: 'Rahmen und Dichtung auf Beschädigungen prüfen' },
    { id: 3, label: 'Neue Scheibe einsetzen', desc: 'Scheibe mit Kleber fixieren und ausrichten' },
    { id: 4, label: 'Aushärtezeit', desc: 'Mindestens 60 Minuten Aushärtezeit einhalten' },
    { id: 5, label: 'Qualitätskontrolle', desc: 'Dichtigkeit und Sitz der Scheibe prüfen' },
  ];

  return (
    <div className="space-y-5">
      {/* Plaketten-Status-Hinweis */}
      {plaketteStatus ? (
        <div
          className="rounded-xl border-2 p-4 flex items-start gap-4"
          style={{ borderColor: plaketteOpt?.color, background: plaketteOpt?.bg }}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: plaketteOpt?.color }}
          >
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[14px] font-bold" style={{ color: plaketteOpt?.color }}>
              Grüne Plakette: {plaketteOpt?.label}
            </div>
            <p className="text-[13px] mt-0.5" style={{ color: plaketteOpt?.color, opacity: 0.8 }}>
              {plaketteStatus === 'ja'
                ? '⚠ Bitte denken Sie daran, die grüne Plakette nach der Montage am Fahrzeug anzubringen!'
                : plaketteOpt?.desc}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-semibold text-amber-800">Plaketten-Status nicht gesetzt</p>
            <p className="text-[12px] text-amber-700 mt-0.5">
              Bitte im Schritt "Scheibe & Material" den Plaketten-Status festlegen.
            </p>
          </div>
        </div>
      )}

      {/* Montage-Schritte */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
            <Wrench className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-[15px] font-bold text-gray-900">Montage-Checkliste</h3>
            <p className="text-[12px] text-gray-500">
              {kennzeichen && <span className="font-mono font-semibold">{kennzeichen}</span>}
              {kennzeichen && ' · '}Scheibenmontage
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {MONTAGE_SCHRITTE.map(schritt => (
            <div
              key={schritt.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer group transition-colors"
              onClick={() => toast(`Schritt ${schritt.id}: ${schritt.label}`, { description: '○ Geplant — Schritt-Tracking kommt bald' })}
            >
              <div className="w-7 h-7 rounded-full border-2 border-gray-300 group-hover:border-emerald-400 flex items-center justify-center flex-shrink-0 transition-colors">
                <span className="text-[11px] font-bold text-gray-400 group-hover:text-emerald-500">{schritt.id}</span>
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-gray-800">{schritt.label}</div>
                <div className="text-[11px] text-gray-500">{schritt.desc}</div>
              </div>
              <Clock className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors" />
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-[12px] text-amber-700">
            <span className="font-semibold">○ Geplant:</span> Schritt-für-Schritt Tracking mit Zeitstempel und Mitarbeiter-Zuordnung.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Social Media Post Button ────────────────────────────────────

function SocialMediaPostButton({ akte }: { akte: any }) {
  const [open, setOpen] = useState(false);

  // Prüfe Instagram-Einwilligung: entweder direkt im Akte-Objekt oder aus localStorage (Kiosk)
  const kioskAuftraege = (() => {
    try { return JSON.parse(localStorage.getItem('kiosk_auftraege_v1') || '[]'); } catch { return []; }
  })();
  const kioskEintrag = kioskAuftraege.find((a: any) =>
    a.kennzeichen && akte.kennzeichen &&
    a.kennzeichen.replace(/\s/g, '').toUpperCase() === akte.kennzeichen.replace(/\s/g, '').toUpperCase()
  );
  const hatEinwilligung = akte.instagram_einwilligung === true || kioskEintrag?.instagramEinwilligung === true;

  if (!hatEinwilligung) return null;

  const fahrzeug = [akte.fahrzeug_marke, akte.fahrzeug_modell].filter(Boolean).join(' ') || akte.kennzeichen || 'Fahrzeug';
  const schadenshoehe = akte.reparaturkosten_brutto
    ? `${Number(akte.reparaturkosten_brutto).toLocaleString('de-DE')} €`
    : akte.wiederbeschaffungswert
    ? `WBW: ${Number(akte.wiederbeschaffungswert).toLocaleString('de-DE')} €`
    : null;
  const instagramHandle = akte.instagram_handle || kioskEintrag?.instagramHandle || '';

  const postText = [
    schadenshoehe ? `${akte.schadenart === 'Totalschaden' ? 'Totalschaden' : 'Unfallschaden'} ${fahrzeug}` : `Schadensfall ${fahrzeug}`,
    schadenshoehe ? `Schadenhöhe: ${schadenshoehe}` : null,
    'Wir haben für unseren Kunden das Maximum rausgeholt! 💪',
    instagramHandle ? `@${instagramHandle}` : null,
    '#Gutachter #Unfallschaden #SachverständigeLüdenscheid #SVBüroKoop #Kfz',
  ].filter(Boolean).join(' — ');

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-pink-600 hover:bg-pink-50 transition-colors border border-pink-200"
        title="Social Media Post erstellen"
      >
        <Instagram className="w-3.5 h-3.5" />
        <span className="hidden lg:inline">Post erstellen</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl border border-pink-200 shadow-xl z-50 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #833AB4, #FD1D1D, #F77737)' }}>
                <Instagram className="w-4 h-4 text-white" />
              </div>
              <span className="text-[13px] font-bold text-gray-800">Social Media Post</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
          </div>

          {/* Fahrzeug-Info */}
          <div className="bg-pink-50 rounded-lg px-3 py-2 mb-3">
            <p className="text-[11px] font-semibold text-pink-700">{fahrzeug}</p>
            {schadenshoehe && <p className="text-[11px] text-pink-600">{schadenshoehe}</p>}
            {instagramHandle && <p className="text-[11px] text-pink-500">@{instagramHandle}</p>}
          </div>

          {/* Post-Text Vorschau */}
          <div className="bg-gray-50 rounded-lg px-3 py-2.5 mb-3">
            <p className="text-[11px] font-semibold text-gray-500 mb-1">Vorgeschlagener Post-Text:</p>
            <p className="text-[12px] text-gray-700 leading-relaxed">{postText}</p>
          </div>

          {/* Aktionen */}
          <div className="space-y-2">
            <button
              onClick={() => {
                navigator.clipboard.writeText(postText).then(() => {
                  toast.success('Post-Text kopiert!', { description: 'Jetzt in Instagram einfügen' });
                  setOpen(false);
                });
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-[12px] font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #833AB4, #FD1D1D, #F77737)' }}
            >
              <Share2 className="w-3.5 h-3.5" />
              Text kopieren
            </button>
            <button
              onClick={() => {
                toast.info('Content-Pipeline geöffnet', { description: 'Post wurde zur Pipeline hinzugefügt' });
                setOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-[12px] font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
            >
              Zur Content-Pipeline
            </button>
          </div>

          <p className="text-[10px] text-gray-400 mt-2 text-center">Kennzeichen wird automatisch unkenntlich gemacht</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────────


export default function AkteDetail() {
  const params = useParams<{ betrieb: string; aktenNr: string; section?: string }>();
  const betriebId = params.betrieb as BetriebId;
  const aktenNr = params.aktenNr;
  const section = params.section || 'uebersicht';
  const betrieb = BETRIEBE[betriebId];
  const { activeAkte, openAkte } = useApp();
  const { user, isAdmin, session } = useAuth();
  const [, setLocation] = useLocation();

  const { data: akte, loading, error, refetch: refetchAkte } = useAkteByNr(aktenNr);
  const { data: svDaten } = useSvAktenDaten(akte?.id || null);
  const { data: textbausteine } = useTextbausteine();
  const [showDokumentGenerator, setShowDokumentGenerator] = useState(false);
  const [winValueDocsReload, setWinValueDocsReload] = useState(0);
  const [showLinkGenerator, setShowLinkGenerator] = useState(false);
  const [showRueckgabeDialog, setShowRueckgabeDialog] = useState(false);
  const [werkstattAnlegenOpen, setWerkstattAnlegenOpen] = useState(false);
  const [mietwagenCreating, setMietwagenCreating] = useState(false);
  const [folgeMenuOpen, setFolgeMenuOpen] = useState(false);
  const [verknuepfenMenuOpen, setVerknuepfenMenuOpen] = useState(false);
  const handleMietwagenAusWinValue = async () => {
    if (!akte || mietwagenCreating) return;
    setMietwagenCreating(true);
    try {
      const res = await fetch('/api/akte-aus-winvalue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'akteAusWinvalue', gutachten_akte_id: akte.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      toast.success('Mietwagen-Akte erstellt', {
        description: json.mietwagen_akten_nummer
          ? `Aktenzeichen: ${json.mietwagen_akten_nummer}`
          : 'Die Akte wurde erfolgreich angelegt.',
      });
      refetchAkte();
    } catch (err) {
      toast.error('Fehler beim Erstellen der Mietwagen-Akte', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setMietwagenCreating(false);
    }
  };
  // Local optimistic state for demontage toggle (Issue #588)
  const [demontageErforderlich, setDemontageErforderlich] = useState<boolean | null>(null);
  // Stellungnahme-Label (Issue #240) — Anzahl der markierten Dokumente dieser Akte
  const [stellungnahmenCount, setStellungnahmenCount] = useState(0);

  // Issue #615: Zahlungseingänge für diese Mietwagen-Akte
  const [zahlungseingaenge, setZahlungseingaenge] = useState<Array<{id: string; betrag: number; datum: string; absender: string | null; zahlungsart: string}>>([]);
  const zahlungseingaengeFetchedForId = useRef<string | null>(null);

  useEffect(() => {
    if (!akte?.id || akte.business_unit !== 'mietwagen') return;
    if (zahlungseingaengeFetchedForId.current === akte.id) return;
    zahlungseingaengeFetchedForId.current = akte.id;
    let cancelled = false;
    (async () => {
      const { data: rechnungen } = await supabase
        .from('rechnungen')
        .select('id')
        .eq('akte_id', akte.id);
      if (cancelled || !rechnungen?.length) return;
      const ids = rechnungen.map((r: { id: string }) => r.id);
      const { data } = await supabase
        .from('zahlungseingaenge')
        .select('id, betrag, datum, absender, zahlungsart')
        .in('rechnung_id', ids)
        .order('datum', { ascending: false });
      if (!cancelled) setZahlungseingaenge(data || []);
    })();
    return () => { cancelled = true; };
  }, [akte?.id, akte?.business_unit]);

  // Issue #615: Live-Stundenuhr für in_miete — tick jede Minute
  const [liveNow, setLiveNow] = useState(() => new Date());
  useEffect(() => {
    if (akte?.mietwagen_status !== 'in_miete') return;
    const timer = setInterval(() => setLiveNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, [akte?.mietwagen_status]);

  // Issue #21: KM-Stand aus Rentsoft (fahrzeuge-Tabelle) für Übergabe-Hinweis
  const [rentsoftFahrzeugKm, setRentsoftFahrzeugKm] = useState<number | null>(null);
  useEffect(() => {
    const kz = akte?.mietfahrzeug_kennzeichen;
    if (!kz || akte?.business_unit !== 'mietwagen') { setRentsoftFahrzeugKm(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('fahrzeuge')
        .select('kennzeichen, km_aktuell')
        .not('km_aktuell', 'is', null);
      if (cancelled || !data) return;
      const normalise = (s: string) => s.replace(/\s+/g, '').toUpperCase();
      const match = data.find((f: { kennzeichen: string | null; km_aktuell: number | null }) =>
        f.kennzeichen && normalise(f.kennzeichen) === normalise(kz),
      );
      if (!cancelled) setRentsoftFahrzeugKm(match?.km_aktuell ?? null);
    })();
    return () => { cancelled = true; };
  }, [akte?.mietfahrzeug_kennzeichen, akte?.business_unit]);

  useEffect(() => {
    if (!akte?.id) { setStellungnahmenCount(0); return; }
    let cancelled = false;
    (async () => {
      const { count, error: countErr } = await supabase
        .from('dokumente')
        .select('*', { count: 'exact', head: true })
        .eq('akte_id', akte.id)
        .contains('tags', [STELLUNGNAHME_LABEL]);
      if (cancelled) return;
      if (countErr) {
        Sentry.captureException(countErr, { extra: { context: 'stellungnahmen-count', akte_id: akte.id } });
        toast.error('Stellungnahmen konnten nicht geladen werden', { description: countErr.message });
        setStellungnahmenCount(0);
        return;
      }
      setStellungnahmenCount(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [akte?.id]);

  const handleToggleStellungnahme = useCallback(async (active: boolean) => {
    if (!akte?.id) return;
    const current = Array.isArray(akte.labels) ? akte.labels : [];
    const next = active
      ? Array.from(new Set([...current, STELLUNGNAHME_LABEL]))
      : current.filter(l => l !== STELLUNGNAHME_LABEL);
    const { error: updErr } = await supabase
      .from('akten')
      .update({ labels: next })
      .eq('id', akte.id);
    if (updErr) {
      toast.error('Stellungnahme-Label konnte nicht gespeichert werden', {
        description: updErr.message,
      });
      return;
    }
    toast.success(active ? 'Als Stellungnahme markiert' : 'Stellungnahme-Label entfernt');
    refetchAkte();
  }, [akte?.id, akte?.labels, refetchAkte]);

  // Demontage toggle handler (Issue #588)
  const handleDemontageToggle = useCallback(async () => {
    if (!akte?.id) return;
    const next = !(demontageErforderlich ?? akte.demontage_erforderlich ?? false);
    setDemontageErforderlich(next);
    const { error: updErr } = await supabase
      .from('akten')
      .update({ demontage_erforderlich: next })
      .eq('id', akte.id);
    if (updErr) {
      setDemontageErforderlich(demontageErforderlich ?? akte.demontage_erforderlich ?? false);
      toast.error('Demontage-Flag konnte nicht gespeichert werden', { description: updErr.message });
    }
  }, [akte?.id, akte?.demontage_erforderlich, demontageErforderlich]);

  // Auto-open akte in context when loaded.
  // Use `aktenIdentifier` (akten_nummer with akten_nr fallback) so the legacy
  // nullable `akten_nr` column never leaks `null` into `activeAkte.aktenNr` —
  // see issue #155. Fall back to the URL param for rows where both columns are
  // missing so the Prozess-Spalte can still look the Akte up.
  useEffect(() => {
    if (!akte) return;
    const identifier = aktenIdentifier(akte) ?? aktenNr;
    if (!identifier) return;
    if (activeAkte && activeAkte.aktenNr === identifier) return;
    const kundenName = akte.kunde_name || akte.titel;
    openAkte({
      id: akte.id,
      aktenNr: identifier,
      titel: akte.titel,
      kundenName,
      businessUnit: akte.business_unit as BusinessUnit,
    });
  }, [akte, activeAkte, openAkte, aktenNr]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Akte wird geladen...</span>
      </div>
    );
  }

  if (error || !akte) {
    return (
      <div className="p-8 text-center min-h-[200px] flex flex-col items-center justify-center">
        <FileText className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">Akte <span className="font-mono font-semibold">{aktenNr}</span> nicht gefunden</p>
        <button
          onClick={() => setLocation(`/akten/${betriebId}`)}
          className="mt-3 text-sm text-[#9B1C1C] hover:underline font-medium"
        >
          Zurück zur Aktenliste
        </button>
      </div>
    );
  }

  const kundenName = akte.kunde_name || akte.titel;
  // Single source of truth for the akten identifier — `akten_nummer` with
  // `akten_nr` fallback, or the URL param when both DB columns are empty.
  // Child views (AutoglasScheibeSection, DokumentGenerator, WinValue panels,
  // Plaketten-Lookup, …) must never receive a literal `null` here.
  const aktenNrSafe = aktenIdentifier(akte) ?? aktenNr;

  const statusLabel = STATUS_LABELS[akte.status as keyof typeof STATUS_LABELS] || akte.status;
  const statusColor = STATUS_COLORS[akte.status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-600';

  // Autoglas-spezifische Sections
  const isAutoglas = betriebId === 'autoglas';
  const isAutoGlasScheibeSection = isAutoglas && section === 'scheibe';
  const isAutoGlasMontageSection = isAutoglas && section === 'montage';
  const isGutachtenSection = !isAutoglas && section === 'gutachten';
  const isDokumenteSection = !isAutoglas && section === 'dokumente';
  const isForderungSection = !isAutoglas && section === 'forderung';
  const isAnwaltSection = !isAutoglas && section === 'anwalt';
  // Gutachten-Prozessschritte (WinValue-synchronisiert, jetzt aktiv)
  const isGutachten = betriebId === 'gutachten';
  // Sachverständigenverfahren (§§ 84, 215 VVG): keine Mietwagen-/Werkstatt-Verknüpfung.
  const istSv = istSvVerfahren(akte.auftragstyp_sv);
  const isGutAuftragSection    = isGutachten && section === 'auftrag';
  const isGutBeteiligteSection = isGutachten && section === 'beteiligte';
  const isGutFahrzeugSection   = isGutachten && section === 'fahrzeug';
  const isGutBesichtigungenSection = isGutachten && section === 'besichtigungen';
  const isGutLichtbilderSection    = isGutachten && section === 'lichtbilder';
  const isGutBewertungSection      = isGutachten && section === 'bewertung';
  const isGutRestwertSection       = isGutachten && section === 'restwert';
  const isGutMinderwertSection     = isGutachten && section === 'minderwert';
  const isGutRechnungSection       = isGutachten && section === 'rechnung';
  const isGutProtokollSection      = isGutachten && section === 'protokoll';
  const isGutStellungnahmeSection  = isGutachten && section === 'stellungnahme';
  const isMietwagen = betriebId === 'mietwagen';
  const isMietwagenAnfrageSection = isMietwagen && section === 'anfrage';
  const isMietwagenVerfuegbarkeitSection = isMietwagen && section === 'verfuegbarkeit';
  const isMietwagenMietvertragSection = isMietwagen && section === 'mietvertrag';
  const isMietwagenUebergabeSection = isMietwagen && section === 'uebergabe';
  const isMietwagenLaufzeitSection = isMietwagen && section === 'laufzeit';
  const isMietwagenRueckgabeSection = isMietwagen && section === 'rueckgabe';
  const isMietwagenSchadenscheckSection = isMietwagen && section === 'schadenscheck';
  const isMietwagenAbrechnungSection = isMietwagen && section === 'abrechnung';
  const isMietwagenKorrespondenzSection = isMietwagen && section === 'korrespondenz';
  const isKnownSection = section === 'uebersicht' || isAutoGlasScheibeSection || isAutoGlasMontageSection || isGutachtenSection || isDokumenteSection || isForderungSection || isAnwaltSection || isGutAuftragSection || isGutBeteiligteSection || isGutFahrzeugSection || isGutBesichtigungenSection || isGutLichtbilderSection || isGutBewertungSection || isGutRestwertSection || isGutMinderwertSection || isGutRechnungSection || isGutProtokollSection || isGutStellungnahmeSection || isMietwagenAnfrageSection || isMietwagenVerfuegbarkeitSection || isMietwagenMietvertragSection || isMietwagenUebergabeSection || isMietwagenLaufzeitSection || isMietwagenRueckgabeSection || isMietwagenSchadenscheckSection || isMietwagenAbrechnungSection || isMietwagenKorrespondenzSection;

  return (
    <div className="flex flex-col h-full">
      {/* ===== AKTEN-KOPFZEILE (schmale Leiste) ===== */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-3 flex-shrink-0">
        {/* Breadcrumb + Aktenzeichen + Name (wie WinValue) */}
        <nav className="flex items-center gap-1 text-[11px] sm:text-[12px] text-gray-400 flex-shrink-0 min-w-0">
          <Link href="/" className="hover:text-gray-600 transition-colors">
            <Home className="w-3.5 h-3.5" />
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link href={`/akten/${betriebId}`} className="hover:text-gray-600 transition-colors">
            Akten
          </Link>
        </nav>
        <span className="text-[14px] font-bold font-mono text-gray-900 flex-shrink-0">{aktenNrSafe}</span>
        <span className="text-[14px] font-semibold text-gray-800 truncate max-w-[160px] sm:max-w-[240px]">{akte.kunde_name || akte.titel}</span>
        {(akte.kennzeichen || akte.fahrzeug_kennzeichen) && (
          <span data-testid="header-kennzeichen" className="inline-flex items-stretch h-6 rounded-md overflow-hidden border border-gray-300 flex-shrink-0">
            <span className="flex flex-col items-center justify-center bg-indigo-600 text-white px-1 leading-none">
              <span className="text-[7px]">★</span>
              <span className="text-[9px] font-bold">D</span>
            </span>
            <span className="px-2 flex items-center font-mono font-bold text-[12px] tracking-wider text-gray-900">{akte.kennzeichen || akte.fahrzeug_kennzeichen}</span>
          </span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Aktions-Buttons */}
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          {/* Aktionen — kombiniertes Dropdown (Folgeakte anlegen + verknüpfen) */}
          {betriebId === 'gutachten' && !istSv && (
            <div className="relative flex-shrink-0">
              <button
                type="button"
                data-testid="aktionen-menu-trigger"
                onClick={() => setFolgeMenuOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                Aktionen
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {folgeMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setFolgeMenuOpen(false)} />
                  <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5">
                    {((akte.winvalue_uid && !akte.mietwagen_akte_id) || !akte.werkstatt_akte_id) && (
                      <div className="text-[10px] uppercase tracking-wide text-gray-400 font-bold px-2.5 pt-1.5 pb-1">Folgeakte anlegen</div>
                    )}
                    {akte.winvalue_uid && !akte.mietwagen_akte_id && (
                      <button
                        type="button"
                        data-testid="folgeakte-mietwagen"
                        disabled={mietwagenCreating}
                        onClick={() => { setFolgeMenuOpen(false); handleMietwagenAusWinValue(); }}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-60"
                      >
                        <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
                          {mietwagenCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Car className="w-3.5 h-3.5" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12px] font-semibold text-gray-800">Mietwagen-Akte</span>
                          <span className="block text-[11px] text-gray-500">Aus WinValue-Dossier übernehmen</span>
                        </span>
                      </button>
                    )}
                    {!akte.werkstatt_akte_id && (
                      <button
                        type="button"
                        data-testid="folgeakte-werkstatt"
                        onClick={() => { setFolgeMenuOpen(false); setWerkstattAnlegenOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-3.5 h-3.5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12px] font-semibold text-gray-800">Werkstatt-Akte</span>
                          <span className="block text-[11px] text-gray-500">Reparaturauftrag anlegen</span>
                        </span>
                      </button>
                    )}
                    <div className="h-px bg-gray-100 my-1.5" />
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 font-bold px-2.5 pt-0.5 pb-1">Mit bestehender Akte verknüpfen</div>
                    <button
                      type="button"
                      data-testid="verknuepfen-mietwagen"
                      onClick={() => { setFolgeMenuOpen(false); toast('Mietwagen-Akte verknüpfen', { description: 'Picker folgt — Backend-Endpoint fehlt noch.' }); }}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0"><Link2 className="w-3.5 h-3.5" /></span>
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold text-gray-800">Mietwagen-Akte verknüpfen</span>
                        <span className="block text-[11px] text-gray-500">Vorhandene Mietwagen-Akte zuordnen</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      data-testid="verknuepfen-werkstatt"
                      onClick={() => { setFolgeMenuOpen(false); toast('Werkstatt-Akte verknüpfen', { description: 'Picker folgt — Backend-Endpoint fehlt noch.' }); }}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0"><Wrench className="w-3.5 h-3.5" /></span>
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold text-gray-800">Werkstatt-Akte verknüpfen</span>
                        <span className="block text-[11px] text-gray-500">Vorhandene Werkstatt-Akte zuordnen</span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {/* WinValue Sync Status-Punkt (kompakt, ersetzt früheres Banner) */}
          {akte.winvalue_uid && (
            akte.winvalue_url ? (
              <a
                href={akte.winvalue_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="winvalue-sync-dot"
                title={`WinValue synchronisiert${akte.winvalue_last_sync ? ' · ' + new Date(akte.winvalue_last_sync).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''} · In WinValue öffnen`}
                aria-label="WinValue synchronisiert"
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
              </a>
            ) : (
              <span
                data-testid="winvalue-sync-dot"
                title={`WinValue synchronisiert${akte.winvalue_last_sync ? ' · ' + new Date(akte.winvalue_last_sync).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}`}
                aria-label="WinValue synchronisiert"
                className="flex items-center justify-center w-8 h-8 flex-shrink-0"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
              </span>
            )
          )}
          <button
            onClick={() => setShowLinkGenerator(true)}
            title="Kunden-Link generieren"
            className="flex items-center gap-1 p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg text-[11px] font-medium text-gray-500 hover:bg-gray-100 transition-colors min-w-[32px] min-h-[32px] justify-center"
          >
            <Link2 className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Link senden</span>
          </button>
          <SocialMediaPostButton akte={akte} />
        </div>
      </div>

      {/* ===== INHALT ===== */}
      <div className="flex-1 overflow-y-auto">
        {/* Sticky Aktenkopfzeile — bleibt beim Scrollen oben kleben (Issue #123) */}
        <StickyAktenkopfzeile
          akte={akte}
          betriebId={betriebId}
          stellungnahmenCount={stellungnahmenCount}
          onToggleStellungnahme={handleToggleStellungnahme}
          onRefetch={refetchAkte}
        />

        <div className="p-3 sm:p-4 lg:p-5 space-y-3 sm:space-y-4">
        {/* Cross-reference banner — shows the linked Mietwagen/Gutachten akte. Im SV-Verfahren ausgeblendet. */}
        {!istSv && (
          <LinkedAkteBanner
            linkedAkteId={akte.linked_akte_id}
            currentBusinessUnit={betriebId}
          />
        )}

        {/* Werkstatt Cross-Link Badge (Issue #500) — pending state when werkstatt_in_anspruch=true. Nicht im SV-Verfahren. */}
        {betriebId === 'gutachten' && !istSv && (
          <WerkstattCrossLinkBadge
            werkstatt_in_anspruch={akte.werkstatt_in_anspruch}
            werkstatt_akte_id={akte.werkstatt_akte_id}
          />
        )}

        {/* Werkstatt-Cross-Link + Demontage-Aufwand (Issues #329, #584) — only inside Gutachten-Akten.
            gutachtenHonorarNetto sources the SV-Büro honorar for the
            Honorar + Auslage = Gesamt breakdown (Issue #584). */}
        {betriebId === 'gutachten' && !istSv && section !== 'stellungnahme' && (
          <WerkstattCrossLinkCard
            werkstattAkteId={akte.werkstatt_akte_id}
            werkstattAutoMatchKandidaten={akte.werkstatt_auto_match_kandidaten}
            gutachtenHonorarNetto={akte.sv_honorar_netto ?? null}
          />
        )}

        {/* WinValue → Mietwagen-Akte Import (Issue #381) — only for WinValue-linked Gutachten-Akten. */}
        {betriebId === 'gutachten' && !istSv && section !== 'stellungnahme' && akte.winvalue_uid && akte.mietwagen_akte_id && (
          <WinValueMietwagenImportCard
            gutachtenAkteId={akte.id}
            winvalueUid={akte.winvalue_uid}
            winvalueRef={akte.winvalue_ref}
            mietwagenAkteId={akte.mietwagen_akte_id}
            onAkteUpdated={refetchAkte}
          />
        )}

        {/* Mietwagen-Cross-Link (Issue #272) — only inside Gutachten-Akten. */}
        {betriebId === 'gutachten' && !istSv && section !== 'stellungnahme' && (
          <MietwagenCrossLinkCard
            gutachtenAkteId={akte.id}
            mietwagenInAnspruch={akte.mietwagen_in_anspruch}
            mietwagenAkteId={akte.mietwagen_akte_id}
            kundeName={akte.kunde_name}
            schadennummer={akte.schadennummer}
            schadendatum={akte.schadendatum ?? akte.schadentag}
            kundeEmail={akte.anspruchsteller_email || akte.kunde_email}
            onAkteUpdated={refetchAkte}
          />
        )}

        {/* Gutachten-Cross-Link (Issue #273) — reverse direction, only in Mietwagen-Akten. */}
        {isMietwagen && (
          <GutachtenCrossLinkCard
            mietwagenAkteId={akte.id}
          />
        )}

        {/* Mietwagen Cross-Link (Issue #501) — shown when mietwagen_in_anspruch is set */}
        {betriebId === 'gutachten' && !istSv && (
          <MietgoCrossLinkBadge
            mietwagen_in_anspruch={akte.mietwagen_in_anspruch}
            mietwagen_akte_id={akte.mietwagen_akte_id}
          />
        )}

        {/* Section Title */}
        <div className="flex items-center gap-2 sm:gap-3">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">
            {SECTION_LABELS[section] || section}
          </h2>
          {!isKnownSection && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">
              ⚙ in Entwicklung
            </span>
          )}
          {isAutoglas && (section === 'scheibe' || section === 'montage') && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              Autoglas
            </span>
          )}
        </div>

        {/* Übersicht */}
        {section === 'uebersicht' && (() => {
          const hasWinValue = !!akte.winvalue_uid;
          const hasCosts = akte.reparaturkosten_brutto != null || akte.wiederbeschaffungswert != null;
          const hasVehicleExt = !!(akte.fahrzeug_vin || akte.fahrzeug_hsn || akte.fahrzeug_km != null);
          const hasSchaden = !!(akte.schadennummer || akte.schadentag || akte.schadenort || akte.besichtigungsdatum);
          const participants = [
            akte.anspruchsteller_name && {
              code: 'AS', label: 'Anspruchsteller', bg: 'bg-emerald-100', text: 'text-emerald-700',
              name: akte.anspruchsteller_name,
              addr: [akte.anspruchsteller_strasse, [akte.anspruchsteller_plz, akte.anspruchsteller_ort].filter(Boolean).join(' ')].filter(Boolean).join(', '),
              phone: akte.anspruchsteller_telefon, email: akte.anspruchsteller_email,
            },
            akte.versicherung && {
              code: 'VN', label: 'Versicherung', bg: 'bg-blue-100', text: 'text-blue-700',
              name: akte.versicherung,
              addr: [akte.versicherung_adresse, [akte.versicherung_plz, akte.versicherung_ort].filter(Boolean).join(' ')].filter(Boolean).join(', '),
              phone: akte.versicherung_telefon, email: akte.versicherung_email,
            },
            akte.gegner_name && {
              code: 'UG', label: 'Unfallgegner', bg: 'bg-red-100', text: 'text-red-700',
              name: akte.gegner_name,
              addr: [akte.gegner_adresse, [akte.gegner_plz, akte.gegner_ort].filter(Boolean).join(' ')].filter(Boolean).join(', '),
            },
            akte.werkstatt_name && {
              code: 'WS', label: 'Werkstatt', bg: 'bg-amber-100', text: 'text-amber-700',
              name: akte.werkstatt_name,
              addr: [akte.werkstatt_adresse, [akte.werkstatt_plz, akte.werkstatt_ort].filter(Boolean).join(' ')].filter(Boolean).join(', '),
              phone: akte.werkstatt_telefon, email: akte.werkstatt_email,
            },
            akte.anwalt_name && {
              code: 'RA', label: 'Rechtsanwalt', bg: 'bg-indigo-100', text: 'text-indigo-700',
              name: akte.anwalt_name,
              addr: [akte.anwalt_adresse, [akte.anwalt_plz, akte.anwalt_ort].filter(Boolean).join(' ')].filter(Boolean).join(', '),
              phone: akte.anwalt_telefon, email: akte.anwalt_email,
            },
          ].filter(Boolean) as Array<{code: string; label: string; bg: string; text: string; name: string; addr: string; phone?: string | null; email?: string | null}>;

          return (
            <div className="space-y-3 sm:space-y-4">
              {/* ── Mietwagen Workflow-Stepper (Issue #474, #615) ── */}
              {isMietwagen && (
                <MietwagenWorkflowStepper
                  akteId={akte.id}
                  status={akte.mietwagen_status}
                  onStatusChanged={refetchAkte}
                  accessToken={session?.access_token ?? null}
                  onUebergabe={() => setLocation(`/akten/${betriebId}/${aktenNrSafe}/uebergabe`)}
                  onRueckgabe={() => setShowRueckgabeDialog(true)}
                  onAbrechnen={() => setLocation(`/akten/${betriebId}/${aktenNrSafe}/abrechnung`)}
                />
              )}

              {/* ── Mietwagen: Direktlink → Rentsoft (Issue #21) ── */}
              {isMietwagen && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 sm:px-4 py-2.5 flex items-center gap-3">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Car className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] font-semibold text-blue-800">Rentsoft Mietwagen</span>
                    <p className="text-[11px] text-blue-500 mt-0.5">Buchungsverwaltung und Fahrzeugdaten</p>
                  </div>
                  <a
                    data-testid="rentsoft-akte-link"
                    href="https://app.v2.rentsoft.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-blue-700 hover:text-blue-900 font-medium flex items-center gap-1 flex-shrink-0"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">In Rentsoft öffnen</span>
                  </a>
                </div>
              )}

              {/* ── Mietwagen: Restschuld-Anzeige (Issue #615) ── */}
              {isMietwagen && (() => {
                const tagessatz = akte.tagespreis ?? 0;
                const mietdauerTage = akte.mietdauer_tage != null
                  ? akte.mietdauer_tage
                  : berechneMietdauerTage(akte.mietbeginn, akte.mietende);
                const zahlungenGesamt = zahlungseingaenge.reduce((s, z) => s + z.betrag, 0);
                const mietkosten = tagessatz * mietdauerTage;
                const restschuld = berechneRestschuld(tagessatz, mietdauerTage, zahlungenGesamt);
                if (tagessatz === 0 && mietdauerTage === 0) return null;
                return (
                  <div
                    data-testid="restschuld-anzeige"
                    className="bg-white rounded-xl border border-gray-200 p-4"
                  >
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Euro className="w-4 h-4 text-gray-500" />
                      Kostenstatus
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Mietkosten</p>
                        <p className="text-[15px] font-bold text-gray-800 font-mono">{formatEuro(mietkosten)}</p>
                        <p className="text-[9px] text-gray-400 mt-0.5">{tagessatz > 0 ? `${formatEuro(tagessatz)}/Tag × ${mietdauerTage} Tage` : '—'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Zahlungen VN</p>
                        <p className="text-[15px] font-bold text-emerald-700 font-mono">{formatEuro(zahlungenGesamt)}</p>
                        <p className="text-[9px] text-gray-400 mt-0.5">{zahlungseingaenge.length} Eingang/Eingänge</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Restschuld</p>
                        <p className={`text-[15px] font-bold font-mono ${restschuld > 0 ? 'text-red-600' : restschuld < 0 ? 'text-emerald-600' : 'text-gray-500'}`}>
                          {formatEuro(Math.abs(restschuld))}{restschuld < 0 ? ' (Guthaben)' : ''}
                        </p>
                        <p className="text-[9px] text-gray-400 mt-0.5">{restschuld === 0 ? 'Vollständig bezahlt' : restschuld > 0 ? 'Noch offen' : 'Überzahlung'}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Mietwagen: Live-Stundenuhr (Issue #615) ── */}
              {isMietwagen && akte.mietwagen_status === 'in_miete' && akte.mietbeginn && (() => {
                const start = new Date(akte.mietbeginn).getTime();
                const now = liveNow.getTime();
                const elapsedMs = Math.max(0, now - start);
                const elapsedH = Math.floor(elapsedMs / (1000 * 60 * 60));
                const elapsedMin = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
                const mietende = akte.mietende ? new Date(akte.mietende).getTime() : null;
                const restMs = mietende ? Math.max(0, mietende - now) : null;
                const restH = restMs != null ? Math.floor(restMs / (1000 * 60 * 60)) : null;
                const restMin = restMs != null ? Math.floor((restMs % (1000 * 60 * 60)) / (1000 * 60)) : null;
                return (
                  <div
                    data-testid="live-stundenuhr"
                    className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Timer className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[12px] font-bold text-blue-800 mb-1">Fahrzeug in Miete</p>
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-[10px] text-blue-500">Vergangen</p>
                          <p className="text-[14px] font-bold text-blue-900 font-mono">{elapsedH}h {elapsedMin}min</p>
                        </div>
                        {restH != null && restMin != null && (
                          <div>
                            <p className="text-[10px] text-blue-500">Bis Mietende</p>
                            <p className={`text-[14px] font-bold font-mono ${restMs != null && restMs < 3600_000 ? 'text-amber-700' : 'text-blue-900'}`}>
                              {restH}h {restMin}min
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {akte.mietende && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-[10px] text-blue-500">Geplantes Mietende</p>
                        <p className="text-[12px] font-semibold text-blue-800">
                          {new Date(akte.mietende).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Mietwagen: Foto-Galerie Übergabe/Rückgabe (Issue #615) ── */}
              {isMietwagen && (akte.uebergabe_fotos_url || akte.rueckgabe_fotos_url) && (
                <div
                  data-testid="foto-galerie"
                  className="bg-white rounded-xl border border-gray-200 p-4"
                >
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-gray-500" />
                    Fahrzeugfotos
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {akte.uebergabe_fotos_url && (
                      <a
                        href={akte.uebergabe_fotos_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors group"
                      >
                        <img
                          src={akte.uebergabe_fotos_url}
                          alt="Übergabefoto"
                          className="w-full h-32 object-cover group-hover:opacity-90 transition-opacity"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <p className="text-[10px] text-gray-500 text-center py-1.5 font-medium">Übergabe</p>
                      </a>
                    )}
                    {akte.rueckgabe_fotos_url && (
                      <a
                        href={akte.rueckgabe_fotos_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors group"
                      >
                        <img
                          src={akte.rueckgabe_fotos_url}
                          alt="Rückgabefoto"
                          className="w-full h-32 object-cover group-hover:opacity-90 transition-opacity"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <p className="text-[10px] text-gray-500 text-center py-1.5 font-medium">Rückgabe</p>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* ── Mietwagen: Zahlungseingangs-Sektion (Issue #615) ── */}
              {isMietwagen && (
                <div
                  data-testid="zahlungseingaenge-sektion"
                  className="bg-white rounded-xl border border-gray-200 p-4"
                >
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-500" />
                    Versicherungs-Zahlungseingänge
                  </h3>
                  {zahlungseingaenge.length === 0 ? (
                    <p className="text-[12px] text-gray-400 text-center py-3">Noch keine Zahlungen eingegangen</p>
                  ) : (
                    <div className="space-y-2">
                      {zahlungseingaenge.map(z => (
                        <div key={z.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                          <div>
                            <p className="text-[12px] font-medium text-gray-800">{z.absender || 'Unbekannt'}</p>
                            <p className="text-[10px] text-gray-400">
                              {new Date(z.datum).toLocaleDateString('de-DE')} · {z.zahlungsart}
                            </p>
                          </div>
                          <span className="text-[13px] font-bold text-emerald-700 font-mono">
                            {formatEuro(z.betrag)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}


              {/* ── Kennzeichen Badge ── */}
              {akte.kennzeichen && hasWinValue && (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="inline-flex items-center border-2 border-blue-600 rounded-md overflow-hidden shadow-sm">
                    <div className="bg-blue-700 text-white w-6 flex flex-col items-center justify-center py-1.5">
                      <span className="text-[7px] leading-none">★</span>
                      <span className="text-[10px] font-bold leading-none mt-0.5">D</span>
                    </div>
                    <div className="px-3 py-0.5 bg-white text-[16px] font-bold font-mono tracking-widest text-gray-900">
                      {akte.kennzeichen}
                    </div>
                  </div>
                  {akte.fahrzeug_marke && (
                    <span className="text-[13px] text-gray-600">
                      {akte.fahrzeug_marke} {akte.fahrzeug_modell || ''}
                      {akte.fahrzeug_submodell ? ` ${akte.fahrzeug_submodell}` : ''}
                    </span>
                  )}
                </div>
              )}

              {/* ── Row 1: Zusammenfassung + Kosten / Zeitverlauf ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {/* Zusammenfassung */}
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Zusammenfassung</h3>
                  <div className="space-y-3">
                    <InfoRow label="Aktenzeichen" value={aktenNrSafe} mono />
                    <InfoRow label="Kunde" value={kundenName} />
                    <InfoRow label="Betrieb" value={betrieb?.name || ''} />
                    <InfoRow label="Status" value={statusLabel} />
                    {akte.auftragstyp_sv && <InfoRow label="Gutachtentyp" value={akte.auftragstyp_sv === 'kasko' ? 'Kaskogutachten' : 'Haftpflichtgutachten'} />}
                    {akte.schadenart && <InfoRow label="Schadensart" value={akte.schadenart} />}
                    {!hasWinValue && akte.kennzeichen && <InfoRow label="Kennzeichen" value={akte.kennzeichen} mono />}
                    {akte.versicherung && <InfoRow label="Versicherung" value={akte.versicherung} />}
                    {akte.schadennummer && <InfoRow label="Schadennummer" value={akte.schadennummer} mono />}
                    {akte.mietbeginn && <InfoRow label="Mietbeginn" value={new Date(akte.mietbeginn).toLocaleDateString('de-DE')} />}
                    {akte.mietende && <InfoRow label="Mietende" value={new Date(akte.mietende).toLocaleDateString('de-DE')} />}
                    {akte.tagespreis != null && <InfoRow label="Tagessatz" value={formatEuro(akte.tagespreis)} />}
                    <InfoRow label="Erstellt am" value={new Date(akte.created_at).toLocaleDateString('de-DE')} />
                  </div>
                </div>

                {/* Kosten-Übersicht (WinValue) or Zeitverlauf (default) */}
                {hasWinValue && hasCosts ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Kosten-Übersicht
                    </h3>
                    <div className="space-y-2.5">
                      {akte.reparaturkosten_brutto != null && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-[12px] text-gray-500">Reparaturkosten (brutto)</span>
                          <span className="text-[14px] font-bold text-gray-900 font-mono">{formatEuro(akte.reparaturkosten_brutto)}</span>
                        </div>
                      )}
                      {akte.reparaturkosten_netto != null && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-[12px] text-gray-500">Reparaturkosten (netto)</span>
                          <span className="text-[13px] text-gray-600 font-mono">{formatEuro(akte.reparaturkosten_netto)}</span>
                        </div>
                      )}
                      {akte.wiederbeschaffungswert != null && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-[12px] text-gray-500">Wiederbeschaffungswert</span>
                          <span className="text-[14px] font-bold text-gray-900 font-mono">{formatEuro(akte.wiederbeschaffungswert)}</span>
                        </div>
                      )}
                      {akte.wiederbeschaffungswert_netto != null && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-[12px] text-gray-500">WBW (netto)</span>
                          <span className="text-[13px] text-gray-600 font-mono">{formatEuro(akte.wiederbeschaffungswert_netto)}</span>
                        </div>
                      )}
                      {akte.minderwert != null && akte.minderwert > 0 && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-[12px] text-gray-500">Merkantile Wertminderung</span>
                          <span className="text-[13px] font-medium text-gray-700 font-mono">{formatEuro(akte.minderwert)}</span>
                        </div>
                      )}
                      {akte.mwst_satz != null && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-[12px] text-gray-500">MwSt</span>
                          <span className="text-[13px] text-gray-600">{akte.mwst_satz}%</span>
                        </div>
                      )}
                    </div>
                    {/* Kostenaufstellung */}
                    {(akte.ersatzteilkosten_netto != null || akte.lohnkosten_netto != null || akte.lackkosten_netto != null || akte.zusatzkosten_netto != null) && (
                      <div className="border-t border-gray-100 mt-3 pt-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Kostenaufstellung (netto)</p>
                        <div className="space-y-1.5">
                          {akte.ersatzteilkosten_netto != null && (
                            <div className="flex justify-between items-baseline">
                              <span className="text-[11px] text-gray-400">Ersatzteile</span>
                              <span className="text-[12px] text-gray-600 font-mono">{formatEuro(akte.ersatzteilkosten_netto)}</span>
                            </div>
                          )}
                          {akte.lohnkosten_netto != null && (
                            <div className="flex justify-between items-baseline">
                              <span className="text-[11px] text-gray-400">Lohnkosten</span>
                              <span className="text-[12px] text-gray-600 font-mono">{formatEuro(akte.lohnkosten_netto)}</span>
                            </div>
                          )}
                          {akte.lackkosten_netto != null && (
                            <div className="flex justify-between items-baseline">
                              <span className="text-[11px] text-gray-400">Lackkosten</span>
                              <span className="text-[12px] text-gray-600 font-mono">{formatEuro(akte.lackkosten_netto)}</span>
                            </div>
                          )}
                          {akte.zusatzkosten_netto != null && (
                            <div className="flex justify-between items-baseline">
                              <span className="text-[11px] text-gray-400">Zusatzkosten</span>
                              <span className="text-[12px] text-gray-600 font-mono">{formatEuro(akte.zusatzkosten_netto)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 sm:mb-4">Zeitverlauf</h3>
                    <div className="space-y-3">
                      <TimelineItem label="Akte angelegt" date={new Date(akte.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} status="fertig" />
                      <TimelineItem label="Auftrag erteilt" date="Ausstehend" status="offen" />
                      {isAutoglas && <TimelineItem label="Scheibentausch" date="Ausstehend" status="offen" />}
                      {isAutoglas && <TimelineItem label="Montage abgeschlossen" date="Ausstehend" status="offen" />}
                      {betriebId === 'mietwagen' && <TimelineItem label="Mietvertrag erstellt" date={akte.mietbeginn ? new Date(akte.mietbeginn).toLocaleDateString('de-DE') : 'Ausstehend'} status={akte.mietbeginn ? 'fertig' : 'offen'} />}
                      {betriebId === 'mietwagen' && <TimelineItem label="Fahrzeug übergeben" date="Ausstehend" status="offen" />}
                      {betriebId === 'mietwagen' && <TimelineItem label="Fahrzeug rückgegeben" date={akte.mietende ? new Date(akte.mietende).toLocaleDateString('de-DE') : 'Ausstehend'} status={akte.mietende ? 'fertig' : 'offen'} />}
                      {betriebId === 'mietwagen' && <TimelineItem label="Abtretung unterschrieben" date="Ausstehend" status="offen" />}
                      {betriebId !== 'mietwagen' && !isAutoglas && <TimelineItem label="Besichtigung" date={akte.besichtigungsdatum ? formatDatum(akte.besichtigungsdatum) : 'Ausstehend'} status={akte.besichtigungsdatum ? 'fertig' : 'offen'} />}
                      {betriebId !== 'mietwagen' && !isAutoglas && <TimelineItem label="Gutachten erstellt" date="Ausstehend" status="offen" />}
                      <TimelineItem label="Rechnung gestellt" date="Ausstehend" status="offen" />
                      {betriebId === 'mietwagen' && <TimelineItem label="Forderung an Versicherung" date="Ausstehend" status="offen" />}
                      <StellungnahmeTimelineItem akte={akte} />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Row 2: Fahrzeug + Schadendetails (WinValue) ── */}
              {hasWinValue && (hasVehicleExt || hasSchaden) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {/* Fahrzeug */}
                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      Fahrzeug
                    </h3>
                    <div className="space-y-3">
                      {akte.fahrzeug_marke && <InfoRow label="Marke / Modell" value={`${akte.fahrzeug_marke} ${akte.fahrzeug_modell || ''}${akte.fahrzeug_submodell ? ` ${akte.fahrzeug_submodell}` : ''}`} />}
                      {akte.fahrzeug_vin && <InfoRow label="FIN" value={akte.fahrzeug_vin} mono />}
                      {(akte.fahrzeug_hsn || akte.fahrzeug_tsn) && <InfoRow label="HSN / TSN" value={`${akte.fahrzeug_hsn || '—'} / ${akte.fahrzeug_tsn || '—'}`} mono />}
                      {akte.fahrzeug_ez && <InfoRow label="Erstzulassung" value={formatDatum(akte.fahrzeug_ez)} />}
                      {akte.fahrzeug_km != null && <InfoRow label="Laufleistung" value={`${akte.fahrzeug_km.toLocaleString('de-DE')} km`} />}
                      {akte.fahrzeug_kw != null && <InfoRow label="Leistung" value={`${akte.fahrzeug_kw} kW (${Math.round(akte.fahrzeug_kw * 1.36)} PS)`} />}
                      {akte.fahrzeug_ccm != null && <InfoRow label="Hubraum" value={`${akte.fahrzeug_ccm.toLocaleString('de-DE')} ccm`} />}
                      {akte.fahrzeug_hu && <InfoRow label="Nächste HU" value={formatDatum(akte.fahrzeug_hu)} />}
                      {akte.fahrzeug_bauart && <InfoRow label="Bauart" value={akte.fahrzeug_bauart} />}
                      {akte.fahrzeug_farbe && <InfoRow label="Farbe" value={akte.fahrzeug_farbe} />}
                    </div>
                  </div>

                  {/* Schadendetails */}
                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Schadendetails
                    </h3>
                    <div className="space-y-3">
                      {akte.schadennummer && <InfoRow label="Schadennummer" value={akte.schadennummer} mono />}
                      {akte.schadentag && <InfoRow label="Schadendatum" value={formatDatum(akte.schadentag)} />}
                      {akte.schadenort && <InfoRow label="Schadenort" value={akte.schadenort} />}
                      {akte.besichtigungsdatum && <InfoRow label="Besichtigung" value={formatDatum(akte.besichtigungsdatum)} />}
                      {akte.auftragsart && <InfoRow label="Auftragsart" value={akte.auftragsart} />}
                      {akte.auftragsdatum && <InfoRow label="Auftragsdatum" value={formatDatum(akte.auftragsdatum)} />}
                      {akte.schadenart && <InfoRow label="Schadenart" value={akte.schadenart} />}
                      {akte.versicherungsnummer && <InfoRow label="Versicherungsnr." value={akte.versicherungsnummer} mono />}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Beteiligte ── */}
              {participants.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Beteiligte
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{participants.length}</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {participants.map((p) => (
                      <div key={p.code} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${p.bg} ${p.text}`}>{p.code}</span>
                          <span className="text-[11px] text-gray-500">{p.label}</span>
                        </div>
                        <p className="text-[13px] font-semibold text-gray-900">{p.name}</p>
                        {p.addr && (
                          <p className="text-[11px] text-gray-500 mt-0.5 flex items-start gap-1">
                            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />{p.addr}
                          </p>
                        )}
                        {(p.phone || p.email) && (
                          <div className="flex flex-wrap items-center gap-3 mt-2">
                            {p.phone && (
                              <a href={`tel:${p.phone}`} className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                <Phone className="w-3 h-3" />{p.phone}
                              </a>
                            )}
                            {p.email && (
                              <a href={`mailto:${p.email}`} className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3 flex-shrink-0" />{p.email}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Dokumente (WinValue) ── */}
              {hasWinValue && (akte.winvalue_documents_count || 0) > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Dokumente
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        {akte.winvalue_documents_count}
                      </span>
                    </h3>
                    <Link
                      href={`/akten/${betriebId}/${aktenNrSafe}/dokumente`}
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                    >
                      Alle Dokumente <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <p className="text-[12px] text-gray-500 mt-2">
                    {akte.winvalue_documents_count} Dokumente in WinValue verfügbar
                  </p>
                </div>
              )}

              {/* ── Zeitverlauf (WinValue — shown as separate card below) ── */}
              {hasWinValue && (
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 sm:mb-4">Zeitverlauf</h3>
                  <div className="space-y-3">
                    <TimelineItem label="Akte angelegt" date={new Date(akte.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} status="fertig" />
                    <TimelineItem label="Auftrag erteilt" date={akte.auftragsdatum ? formatDatum(akte.auftragsdatum) : 'Ausstehend'} status={akte.auftragsdatum ? 'fertig' : 'offen'} />
                    <TimelineItem label="Besichtigung" date={akte.besichtigungsdatum ? formatDatum(akte.besichtigungsdatum) : 'Ausstehend'} status={akte.besichtigungsdatum ? 'fertig' : 'offen'} />
                    <TimelineItem label="Gutachten erstellt" date="Ausstehend" status="offen" />
                    <TimelineItem label="Rechnung gestellt" date="Ausstehend" status="offen" />
                    <StellungnahmeTimelineItem akte={akte} />
                  </div>
                </div>
              )}

              {/* Autoglas: Plaketten-Schnellinfo */}
              {isAutoglas && (() => {
                const pd = loadPlaketteData();
                const ps = pd[aktenNrSafe];
                const po = PLAKETTE_OPTIONS.find(o => o.value === ps);
                return ps ? (
                  <div
                    className="rounded-xl border-2 p-4 flex items-center gap-4"
                    style={{ borderColor: po?.color, background: po?.bg }}
                  >
                    <Shield className="w-6 h-6 flex-shrink-0" style={{ color: po?.color }} />
                    <div>
                      <span className="text-[13px] font-bold" style={{ color: po?.color }}>Grüne Plakette: {po?.label}</span>
                      <p className="text-[12px] mt-0.5" style={{ color: po?.color, opacity: 0.8 }}>{po?.desc}</p>
                    </div>
                    <Link
                      href={`/akten/${betriebId}/${aktenNrSafe}/scheibe`}
                      className="ml-auto text-[12px] font-semibold underline"
                      style={{ color: po?.color }}
                    >
                      Ändern
                    </Link>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-[13px] font-semibold text-amber-800">Grüne Plakette: Status nicht gesetzt</p>
                      <p className="text-[12px] text-amber-700">
                        Bitte im Schritt{' '}
                        <Link href={`/akten/${betriebId}/${aktenNrSafe}/scheibe`} className="underline font-semibold">
                          Scheibe & Material
                        </Link>{' '}
                        den Plaketten-Status festlegen.
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Autoglas: Scheibe-Section */}
        {isAutoGlasScheibeSection && (
          <AutoglasScheibeSection aktenNr={aktenNrSafe} />
        )}

        {/* Autoglas: Montage-Section */}
        {isAutoGlasMontageSection && (
          <AutoglasMontageSection aktenNr={aktenNrSafe} kennzeichen={akte.kennzeichen || undefined} />
        )}

        {/* Gutachten-Editor */}
        {isGutachtenSection && (() => {
          const halterKontakt = svDaten?.kontakte?.find((k: any) => k.rolle === 'halter') || svDaten?.kontakte?.[0];
          const fzg = svDaten?.fahrzeuge?.[0];
          const bes = svDaten?.besichtigungen?.[0];
          const bew = svDaten?.bewertungen?.[0];
          const kalk = svDaten?.kalkulationen?.[0];
          return (
            <div className="h-[calc(100vh-140px)] sm:h-[calc(100vh-180px)]">
              <GutachtenEditor
                aktenNr={aktenNrSafe}
                aktenDaten={{
                  aktenNr: aktenNrSafe,
                  auftragsDatum: new Date(akte.created_at).toLocaleDateString('de-DE'),
                  gutachtenTyp: akte.auftragstyp_sv === 'kasko' ? 'Kaskogutachten' : 'Haftpflichtgutachten',
                  halter: {
                    anrede: halterKontakt?.anrede || 'Herr',
                    vorname: halterKontakt?.vorname || (akte.kunde_name || '').split(' ')[0] || '',
                    nachname: halterKontakt?.nachname || (akte.kunde_name || '').split(' ').slice(1).join(' ') || '',
                    strasse: halterKontakt?.strasse || '',
                    plz: halterKontakt?.plz || '',
                    ort: halterKontakt?.ort || '',
                    land: halterKontakt?.land || 'Deutschland',
                  },
                  fahrzeug: {
                    kennzeichen: fzg?.kennzeichen || akte.kennzeichen || '',
                    fin: fzg?.fin || '',
                    hsnTsn: fzg?.hsn_tsn || '',
                    hersteller: fzg?.hersteller || akte.fahrzeug_marke || '',
                    modell: fzg?.modell || akte.fahrzeug_modell || '',
                    typ: fzg?.typ || '',
                    bauart: fzg?.bauart || '',
                    farbe: fzg?.farbe || '',
                    erstzulassung: fzg?.erstzulassung || '',
                    laufleistung: fzg?.laufleistung_km ? `${fzg.laufleistung_km.toLocaleString('de-DE')} km` : '',
                    naechsteHU: fzg?.naechste_hu || '',
                    kraftstoff: fzg?.kraftstoff || '',
                    leistungKw: fzg?.leistung_kw ? `${fzg.leistung_kw}` : '',
                    hubraum: fzg?.hubraum_ccm ? `${fzg.hubraum_ccm}` : '',
                    getriebe: fzg?.getriebe || '',
                    antrieb: fzg?.antrieb || '',
                  },
                  besichtigung: {
                    datum: bes?.datum || '',
                    uhrzeit: bes?.uhrzeit || '',
                    ort: bes?.ort || '',
                    firma: bes?.firma || '',
                    adresse: bes?.adresse || '',
                    besichtigtDurch: bes?.besichtigt_durch || 'Andreas Koop',
                  },
                  bewertung: {
                    wiederbeschaffungswert: bew?.wiederbeschaffungswert ? `${bew.wiederbeschaffungswert}` : '',
                    restwert: bew?.restwert ? `${bew.restwert}` : '',
                    reparaturkosten: kalk?.reparaturkosten_brutto ? `${kalk.reparaturkosten_brutto}` : (akte.reparaturkosten_brutto ? `${akte.reparaturkosten_brutto}` : ''),
                    wertminderung: akte.merkantile_wertminderung ? `${akte.merkantile_wertminderung}` : '',
                    nutzungsausfall: akte.nutzungsausfall_betrag ? `${akte.nutzungsausfall_betrag}` : '',
                  },
                  schadenBeschreibung: akte.schadenart || svDaten?.schadenpositionen?.map((p: any) => p.bezeichnung).join(', ') || '',
                }}
                
              />
            </div>
          );
        })()}
        {/* Dokumente zusammenstellen */}
        {isDokumenteSection && (
          <div className="space-y-4">
            {betriebId === 'mietwagen' ? (
              /* ===== MIETWAGEN: Individuelle Dokumentenstruktur ===== */
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900">Mietwagen-Dokumente</h3>
                  <button
                    onClick={() => setShowDokumentGenerator(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-[11px] font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Neues Dokument erstellen
                  </button>
                </div>
                {showDokumentGenerator ? (
                  <div className="h-[calc(100vh-180px)] bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <DokumentGenerator
                      aktenNr={aktenNrSafe}
                      empfaenger={{ name: akte.kunde_name || '' }}
                      aktenDaten={{
                        aktenzeichen: aktenNrSafe,
                        kennzeichen: akte.kennzeichen || akte.fahrzeug_kennzeichen || '',
                        fin: akte.fin || akte.fahrzeug_vin || '',
                        schadennummer: akte.schadennummer || '',
                        versicherung: akte.versicherung || '',
                        versicherungsnummer: akte.versicherungsnummer || '',
                        schadenort: akte.schadenort || '',
                        schadentag: akte.schadentag || akte.schadendatum || '',
                        kennzeichen_gegner: akte.kennzeichen_gegner || '',
                        auftragsdatum: akte.auftragsdatum || '',
                        kunde_name: akte.kunde_name || '',
                      }}
                      onClose={() => setShowDokumentGenerator(false)}
                      onSave={(html, filename) => {
                        toast.success('Dokument gespeichert', { description: filename });
                        setShowDokumentGenerator(false);
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Neue DokumenteSection für Mietwagen */}
                    <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-5">
                      <DokumenteSection
                        akteId={akte.id}
                        aktenNr={aktenNrSafe}
                        betriebId={betriebId}
                        reloadKey={winValueDocsReload}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* ===== GUTACHTEN/AUTOGLAS: Standard-Dokumentenstruktur ===== */
              <>
                {showDokumentGenerator ? (
                  <div className="h-[calc(100vh-180px)] bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <DokumentGenerator
                      aktenNr={aktenNrSafe}
                      empfaenger={{ name: akte.kunde_name || '' }}
                      aktenDaten={{
                        aktenzeichen: aktenNrSafe,
                        kennzeichen: akte.kennzeichen || akte.fahrzeug_kennzeichen || '',
                        fin: akte.fin || akte.fahrzeug_vin || '',
                        schadennummer: akte.schadennummer || '',
                        versicherung: akte.versicherung || '',
                        versicherungsnummer: akte.versicherungsnummer || '',
                        schadenort: akte.schadenort || '',
                        schadentag: akte.schadentag || akte.schadendatum || '',
                        kennzeichen_gegner: akte.kennzeichen_gegner || '',
                        auftragsdatum: akte.auftragsdatum || '',
                        kunde_name: akte.kunde_name || '',
                      }}
                      onClose={() => setShowDokumentGenerator(false)}
                      onSave={(html, filename) => {
                        toast.success('Dokument gespeichert', { description: filename });
                        setShowDokumentGenerator(false);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                      <div />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowDokumentGenerator(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-[11px] font-medium hover:bg-gray-800 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Neues Dokument
                        </button>
                        {akte.winvalue_uid && (
                          <DownloadFromWinValueButton akte={akte} onDone={() => setWinValueDocsReload(v => v + 1)} />
                        )}
                      </div>
                    </div>

                    {/* Neue DokumenteSection mit Sticky Header, Filter, Auswahl, Versand */}
                    <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-5">
                      <DokumenteSection
                        akteId={akte.id}
                        aktenNr={aktenNrSafe}
                        betriebId={betriebId}
                        reloadKey={winValueDocsReload}
                      />
                    </div>

                    <AktenOrdner aktenNr={aktenNrSafe} betriebId={betriebId} userId={user?.id ?? null} istAdmin={isAdmin} />
                    <div className="mt-4">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">Gutachten zusammenstellen</h3>
                      <div className="h-[calc(100vh-500px)] sm:h-[calc(100vh-540px)] min-h-[300px]">
                        <DokumentenZusammenfuehrung aktenNr={aktenNrSafe} />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {/* ===== FORDERUNG & KÜRZUNG SECTION ===== */}
        {isForderungSection && (() => {
          const gutachtenBetrag = (akte.reparaturkosten_brutto || akte.reparaturkosten_netto || 0);
          const minderwert = akte.merkantile_wertminderung || akte.minderwert || 0;
          const svKosten = akte.sv_honorar_netto || 0;
          const nebenkosten = akte.nebenkosten || 0;
          const versicherungGezahlt = akte.gesamtbetrag || 0;
          const versicherung = akte.versicherung || 'Nicht angegeben';
          const schadennr = akte.schadennummer || '—';
          const isProblemfall = akte.notizen?.includes('PROBLEMFALL') || akte.status_sv === 'kuerzung_pruefen';
          
          return (
            <div className="space-y-4">
              {/* Problemfall-Banner */}
              {isProblemfall && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[13px] font-bold text-amber-800">Problemfall — Kürzung durch Versicherung</p>
                    <p className="text-[12px] text-amber-700 mt-1">
                      Die Versicherung hat Positionen gekürzt. Bitte Abrechnungsschreiben prüfen und ggf. Anwalt einschalten.
                    </p>
                  </div>
                </div>
              )}

              {/* Versicherungs-Info */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Versicherung
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] text-gray-500">Versicherung</p>
                    <p className="text-[13px] font-semibold text-gray-900">{versicherung}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] text-gray-500">Schadennummer</p>
                    <p className="text-[13px] font-semibold text-gray-900 font-mono">{schadennr}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] text-gray-500">Schadendatum</p>
                    <p className="text-[13px] font-semibold text-gray-900">
                      {akte.schadendatum ? new Date(akte.schadendatum).toLocaleDateString('de-DE') : '—'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] text-gray-500">Gezahlt von Versicherung</p>
                    <p className="text-[13px] font-semibold text-green-700">
                      {versicherungGezahlt ? `${versicherungGezahlt.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Abrechnungs-Vergleich */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Scale className="w-4 h-4" /> Abrechnungs-Vergleich
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-gray-500 font-medium">Position</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Gutachten</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Versicherung</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Differenz</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-2 text-gray-900">Reparaturkosten</td>
                        <td className="py-2 text-right font-mono">{gutachtenBetrag ? `${gutachtenBetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono text-gray-400">—</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-gray-900">Wertminderung</td>
                        <td className="py-2 text-right font-mono">{minderwert ? `${minderwert.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono text-red-600">Gekürzt</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-gray-900">SV-Kosten</td>
                        <td className="py-2 text-right font-mono">{svKosten ? `${svKosten.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono text-red-600">Gekürzt</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-gray-900">Nebenkosten</td>
                        <td className="py-2 text-right font-mono">{nebenkosten ? `${nebenkosten.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono text-red-600">Gekürzt</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-bold">
                        <td className="py-2 text-gray-900">Gesamt</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono text-green-700">
                          {versicherungGezahlt ? `${versicherungGezahlt.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—'}
                        </td>
                        <td className="py-2 text-right font-mono text-red-600">—</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-[11px] text-gray-400 mt-3">
                  Tragen Sie die Beträge aus dem Abrechnungsschreiben der Versicherung ein, um die Differenz zu berechnen.
                </p>
              </div>

              {/* Abrechnungsschreiben Upload */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Abrechnungsschreiben
                </h3>
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-[12px] text-gray-500">Abrechnungsschreiben hier hochladen</p>
                  <p className="text-[11px] text-gray-400 mt-1">PDF, JPG oder PNG</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Schnellaktionen</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      toast.info('Anwalt-Anschreiben wird vorbereitet...');
                      setLocation(`/akten/${betriebId}/${aktenNrSafe}/anwalt`);
                    }}
                    className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 text-blue-700 rounded-lg text-[12px] font-medium hover:bg-blue-100 transition-colors"
                  >
                    <Scale className="w-4 h-4" />
                    Anwalt anschreiben
                  </button>
                  <button
                    onClick={() => toast.info('Funktion wird entwickelt')}
                    className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 text-amber-700 rounded-lg text-[12px] font-medium hover:bg-amber-100 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    Abrechnungsschreiben anfordern
                  </button>
                  <button
                    onClick={() => toast.info('Funktion wird entwickelt')}
                    className="flex items-center gap-2 px-3 py-2.5 bg-green-50 text-green-700 rounded-lg text-[12px] font-medium hover:bg-green-100 transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Kürzung akzeptieren
                  </button>
                  <button
                    onClick={() => toast.info('Funktion wird entwickelt')}
                    className="flex items-center gap-2 px-3 py-2.5 bg-red-50 text-red-700 rounded-lg text-[12px] font-medium hover:bg-red-100 transition-colors"
                  >
                    <TrendingDown className="w-4 h-4" />
                    Kürzung widersprechen
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===== ANWALT SECTION ===== */}
        {isAnwaltSection && (() => {
          const anwaltInfo = akte.notizen?.match(/Anwalt:\s*(.+)/)?.[1] || '';
          const versicherung = akte.versicherung || 'Nicht angegeben';
          const schadennr = akte.schadennummer || '—';
          const kundeName = akte.kunde_name || '—';
          
          return (
            <div className="space-y-4">
              {/* Anwalt-Info */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Scale className="w-4 h-4" /> Anwalt-Information
                </h3>
                {anwaltInfo ? (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-[13px] font-semibold text-blue-900">{anwaltInfo}</p>
                    <p className="text-[11px] text-blue-600 mt-1">Verknüpfter Anwalt für diesen Fall</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <Scale className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-[12px] text-gray-500">Kein Anwalt verknüpft</p>
                    <p className="text-[11px] text-gray-400 mt-1">Fügen Sie einen Anwalt über die Akte-Bearbeitung hinzu</p>
                  </div>
                )}
              </div>

              {/* Anwalt-Anschreiben Vorlage */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Anschreiben an Anwalt
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 text-[12px] text-gray-700 space-y-2 font-mono">
                  <p>Sehr geehrte Damen und Herren,</p>
                  <p className="mt-2">
                    in der Schadensache <strong>{kundeName}</strong> (Schadennummer: <strong>{schadennr}</strong>) 
                    bei der <strong>{versicherung}</strong> bitten wir Sie höflich, uns das vollständige 
                    Abrechnungsschreiben der Versicherung zukommen zu lassen.
                  </p>
                  <p>
                    Die Versicherung hat unsere Sachverständigenkosten sowie die merkantile Wertminderung 
                    gekürzt. Wir bitten Sie, die vollständigen Ansprüche gegenüber der Versicherung 
                    durchzusetzen. Das Sachverständigenrisiko-Prinzip (BGH) ist hier einschlägig.
                  </p>
                  <p>
                    Bezüglich der merkantilen Wertminderung möchten wir darauf hinweisen, dass die 
                    Ermittlung gemäß § 287 ZPO eine Sache der sachverständigen Schätzung ist und nicht 
                    einer starren Berechnung unterliegt (BGH-Rechtsprechung).
                  </p>
                  <p className="mt-2">Mit freundlichen Grüßen<br/>Andreas Koop<br/>Kfz-Sachverständiger</p>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => {
                      const text = `Sehr geehrte Damen und Herren,\n\nin der Schadensache ${kundeName} (Schadennummer: ${schadennr}) bei der ${versicherung} bitten wir Sie höflich, uns das vollständige Abrechnungsschreiben der Versicherung zukommen zu lassen.\n\nDie Versicherung hat unsere Sachverständigenkosten sowie die merkantile Wertminderung gekürzt. Wir bitten Sie, die vollständigen Ansprüche gegenüber der Versicherung durchzusetzen.\n\nMit freundlichen Grüßen\nAndreas Koop\nKfz-Sachverständiger`;
                      navigator.clipboard.writeText(text);
                      toast.success('Anschreiben in Zwischenablage kopiert');
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-[11px] font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Kopieren
                  </button>
                  <button
                    onClick={() => toast.info('E-Mail-Versand wird vorbereitet...')}
                    className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-[11px] font-medium hover:bg-green-700 transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Per E-Mail senden
                  </button>
                  <button
                    onClick={() => {
                      setShowDokumentGenerator(true);
                      setLocation(`/akten/${betriebId}/${aktenNr}/dokumente`);
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 text-white rounded-lg text-[11px] font-medium hover:bg-gray-800 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Als Brief erstellen
                  </button>
                </div>
              </div>

              {/* Anwalts-Empfehlung */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Anwalts-Empfehlung</h3>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <p className="text-[12px] text-gray-700 mb-3">
                    <strong>Vorteile eines Anwalts:</strong> Der Anwalt übernimmt die gesamte Korrespondenz 
                    mit der Versicherung. Sie können sich zurücklehnen — der Anwalt kümmert sich um alles.
                  </p>
                  <p className="text-[12px] text-gray-700 mb-3">
                    Die Kosten des Anwalts werden in der Regel von der gegnerischen Versicherung getragen 
                    (Haftpflichtfall).
                  </p>
                  <button
                    onClick={() => toast.info('Anwalts-Empfehlung wird geladen...')}
                    className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-[11px] font-medium hover:bg-indigo-700 transition-colors"
                  >
                    <Scale className="w-3.5 h-3.5" />
                    Unsere Anwalts-Empfehlung
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ═══════ MIETWAGEN: Anfrage-Section ═══════ */}
        {isMietwagenAnfrageSection && (
          <MietwagenAnfrageSection akte={akte} onUpdate={() => window.location.reload()} />
        )}

        {/* ═══════ MIETWAGEN: Verfügbarkeit ═══════ */}
        {isMietwagenVerfuegbarkeitSection && (
          <MietwagenEditableSection
            title="Fahrzeugverfügbarkeit"
            description="Geschädigtes Fahrzeug und Verfügbarkeitsprüfung"
            akte={akte}
            onUpdate={() => window.location.reload()}
            infoHint="Die Fahrzeugklasse des geschädigten Fahrzeugs bestimmt die Mietwagenklasse. Bei Totalschaden oder nicht fahrbereitem Fahrzeug besteht sofortiger Anspruch."
            fields={[
              { key: 'fahrzeug_marke', label: 'Marke', type: 'text', placeholder: 'z.B. BMW', group: 'Geschädigtes Fahrzeug' },
              { key: 'fahrzeug_modell', label: 'Modell', type: 'text', placeholder: 'z.B. 320d', group: 'Geschädigtes Fahrzeug' },
              { key: 'kennzeichen', label: 'Kennzeichen', type: 'text', placeholder: 'z.B. MK-AB 123', group: 'Geschädigtes Fahrzeug' },
              { key: 'fahrzeug_klasse', label: 'Fahrzeugklasse', type: 'select', options: [
                { value: '1', label: 'Klasse 1 — Kleinwagen' },
                { value: '2', label: 'Klasse 2 — Kompaktklasse' },
                { value: '3', label: 'Klasse 3 — Mittelklasse' },
                { value: '4', label: 'Klasse 4 — Obere Mittelklasse' },
                { value: '5', label: 'Klasse 5 — Oberklasse' },
                { value: '6', label: 'Klasse 6 — SUV / Geländewagen' },
                { value: '7', label: 'Klasse 7 — Transporter / Van' },
              ], group: 'Geschädigtes Fahrzeug' },
              { key: 'fahrzeug_fahrbereit', label: 'Fahrbereit', type: 'checkbox', placeholder: 'Fahrzeug ist fahrbereit', group: 'Status' },
              { key: 'fahrzeug_totalschaden', label: 'Totalschaden', type: 'checkbox', placeholder: 'Wirtschaftlicher Totalschaden', group: 'Status' },
              { key: 'fahrzeug_verkehrssicher', label: 'Verkehrssicher', type: 'checkbox', placeholder: 'Fahrzeug ist verkehrssicher', group: 'Status' },
              { key: 'notreparatur_moeglich', label: 'Notreparatur möglich', type: 'checkbox', placeholder: 'Notreparatur wäre möglich gewesen', group: 'Status' },
            ]}
          />
        )}

        {/* ═══════ MIETWAGEN: Mietvertrag ═══════ */}
        {isMietwagenMietvertragSection && (
          <div className="space-y-4">
            {/* Mietvertrag-Daten */}
            <MietwagenVertragWizard akte={akte} onUpdate={() => window.location.reload()} />
          </div>
        )}

        {/* ═══════ MIETWAGEN: Fahrzeugübergabe ═══════ */}
        {isMietwagenUebergabeSection && (
          <div className="space-y-3">
            {rentsoftFahrzeugKm != null && (
              <div
                data-testid="rentsoft-km-hinweis"
                className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5"
              >
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-[12px] font-semibold text-blue-800">Rentsoft KM-Stand (letzter Sync): </span>
                  <span className="text-[12px] font-mono text-blue-700">{rentsoftFahrzeugKm.toLocaleString('de-DE')} km</span>
                </div>
                <a
                  href="https://app.v2.rentsoft.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 flex-shrink-0"
                >
                  <ExternalLink className="w-3 h-3" />
                  Rentsoft
                </a>
              </div>
            )}
            <MietwagenEditableSection
              title="Fahrzeugübergabe"
            description="Mietfahrzeug-Daten und Übergabeprotokoll"
            akte={akte}
            onUpdate={async () => {
              // Semantisch korrekt: Fahrzeugübergabe speichern = Schlüsselübergabe-Ereignis → uebergeben.
              // Nur bei vertrag_offen, damit ein erneutes Speichern keinen Rückwärts-Übergang auslöst (Issue #726).
              if (akte.mietwagen_status === 'vertrag_offen' && akte.id) {
                try {
                  const wfRes = await fetch('/api/mietwagen-workflow', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
                    },
                    body: JSON.stringify({ action: 'setzeStatus', mietwagen_akte_id: akte.id, neuer_status: 'uebergeben' }),
                  });
                  if (!wfRes.ok) {
                    toast.error('Status-Übergang fehlgeschlagen — bitte erneut versuchen.');
                    return;
                  }
                } catch {
                  toast.error('Netzwerkfehler beim Status-Übergang — bitte erneut versuchen.');
                  return;
                }
              }
              window.location.reload();
            }}
            fields={[
              { key: 'mietfahrzeug_marke', label: 'Marke', type: 'text', placeholder: 'z.B. VW', group: 'Mietfahrzeug' },
              { key: 'mietfahrzeug_modell', label: 'Modell', type: 'text', placeholder: 'z.B. Golf', group: 'Mietfahrzeug' },
              { key: 'mietfahrzeug_kennzeichen', label: 'Kennzeichen', type: 'text', placeholder: 'z.B. MK-MW 100', group: 'Mietfahrzeug' },
              { key: 'mietfahrzeug_klasse', label: 'Fahrzeugklasse', type: 'select', options: [
                { value: '1', label: 'Klasse 1 — Kleinwagen' },
                { value: '2', label: 'Klasse 2 — Kompaktklasse' },
                { value: '3', label: 'Klasse 3 — Mittelklasse' },
                { value: '4', label: 'Klasse 4 — Obere Mittelklasse' },
                { value: '5', label: 'Klasse 5 — Oberklasse' },
                { value: '6', label: 'Klasse 6 — SUV / Geländewagen' },
                { value: '7', label: 'Klasse 7 — Transporter / Van' },
              ], group: 'Mietfahrzeug' },
              { key: 'uebergabe_datum', label: 'Übergabedatum', type: 'date', group: 'Übergabe' },
              { key: 'uebergabe_ort', label: 'Übergabeort', type: 'text', placeholder: 'z.B. Altena, Rahmedestr. 3', group: 'Übergabe' },
              { key: 'mietfahrzeug_km_start', label: 'KM-Stand bei Übergabe', type: 'number', suffix: 'km', group: 'Übergabe' },
              { key: 'mietfahrzeug_tankstand_start', label: 'Tankstand bei Übergabe', type: 'select', options: [
                { value: 'voll', label: 'Voll' },
                { value: '3/4', label: '3/4' },
                { value: '1/2', label: '1/2' },
                { value: '1/4', label: '1/4' },
                { value: 'leer', label: 'Leer' },
              ], group: 'Übergabe' },
            ]}
            />
          </div>
        )}

        {/* ═══════ MIETWAGEN: Laufzeit & Verlängerung ═══════ */}
        {isMietwagenLaufzeitSection && (
          <MietwagenEditableSection
            title="Laufzeit & Verlängerung"
            description="Mietdauer, Reparaturdauer und Begründung der Mietdauer"
            akte={akte}
            onUpdate={() => window.location.reload()}
            infoHint="Die Mietdauer muss gegenüber der Versicherung begründet werden. Relevante Faktoren: Reparaturdauer, Zerlegungsdauer für Begutachtung, Verkehrssicherheit, Notreparatur."
            fields={[
              { key: 'mietbeginn', label: 'Mietbeginn', type: 'date', group: 'Mietdauer' },
              { key: 'mietende', label: 'Mietende', type: 'date', group: 'Mietdauer' },
              { key: 'mietdauer_tage', label: 'Mietdauer (Tage)', type: 'number', suffix: 'Tage', group: 'Mietdauer' },
              { key: 'voraussichtliche_reparaturdauer', label: 'Voraussichtl. Reparaturdauer', type: 'number', suffix: 'Tage', group: 'Reparatur' },
              { key: 'zerlegungsdauer_tage', label: 'Zerlegungsdauer für Begutachtung', type: 'number', suffix: 'Tage', placeholder: 'Tage für Zerlegung/Begutachtung', group: 'Reparatur' },
              { key: 'mietdauer_begruendung', label: 'Begründung der Mietdauer', type: 'textarea', placeholder: 'z.B. Fahrzeug war nicht verkehrssicher, Stoßfänger musste für Begutachtung zerlegt werden...', colSpan: 2, group: 'Begründung' },
              { key: 'fahrzeug_verkehrssicher', label: 'Fahrzeug verkehrssicher', type: 'checkbox', placeholder: 'Fahrzeug war während Mietdauer verkehrssicher', group: 'Begründung' },
              { key: 'notreparatur_moeglich', label: 'Notreparatur möglich', type: 'checkbox', placeholder: 'Notreparatur hätte stattfinden können', group: 'Begründung' },
            ]}
          />
        )}

        {/* ═══════ MIETWAGEN: Rückgabe ═══════ */}
        {isMietwagenRueckgabeSection && (
          <div className="space-y-4">
            {!akte.rueckgabe_datum && akte.id && (
              <MietwagenRueckgabeProtokoll
                akteId={akte.id}
                onComplete={refetchAkte}
              />
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowRueckgabeDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-semibold shadow-sm"
              >
                Rückgabe erfassen
              </button>
            </div>
            <MietwagenEditableSection
              title="Fahrzeugrückgabe"
              description="Rückgabeprotokoll und Fahrzeugzustand"
              akte={akte}
              onUpdate={() => window.location.reload()}
              fields={[
                { key: 'rueckgabe_datum', label: 'Rückgabedatum', type: 'date', group: 'Rückgabe' },
                { key: 'rueckgabe_ort', label: 'Rückgabeort', type: 'text', placeholder: 'z.B. Altena, Rahmedestr. 3', group: 'Rückgabe' },
                { key: 'mietfahrzeug_km_ende', label: 'KM-Stand bei Rückgabe', type: 'number', suffix: 'km', group: 'Fahrzeugzustand' },
                { key: 'mietfahrzeug_tankstand_ende', label: 'Tankstand bei Rückgabe', type: 'select', options: [
                  { value: 'voll', label: 'Voll' },
                  { value: '3/4', label: '3/4' },
                  { value: '1/2', label: '1/2' },
                  { value: '1/4', label: '1/4' },
                  { value: 'leer', label: 'Leer' },
                ], group: 'Fahrzeugzustand' },
                { key: 'rueckgabe_schaeden', label: 'Schäden bei Rückgabe', type: 'textarea', placeholder: 'Beschreibung eventueller Schäden am Mietfahrzeug...', colSpan: 2, group: 'Fahrzeugzustand' },
              ]}
            />
            {showRueckgabeDialog && (
              <div
                className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto p-4 sm:p-8"
                role="dialog"
                aria-modal="true"
                aria-label="Mietwagen-Rückgabe erfassen"
              >
                <div className="w-full max-w-3xl">
                  <MietwagenRueckgabe
                    akte={akte}
                    onClose={() => setShowRueckgabeDialog(false)}
                    onComplete={() => {
                      setShowRueckgabeDialog(false);
                      refetchAkte();
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════ MIETWAGEN: Schadenscheck ═══════ */}
        {isMietwagenSchadenscheckSection && (
          <MietwagenEditableSection
            title="Schadenscheck"
            description="Prüfung des Mietfahrzeugs auf Schäden nach Rückgabe"
            akte={akte}
            onUpdate={() => window.location.reload()}
            fields={[
              { key: 'rueckgabe_schaeden', label: 'Rückgabe-Schäden', type: 'textarea', placeholder: 'Beschreibung der Schäden am Mietfahrzeug...', colSpan: 2, group: 'Schadensprüfung' },
              { key: 'schadenscheck_ergebnis', label: 'Prüfergebnis', type: 'select', options: [
                { value: 'ok', label: 'Keine Schäden — Fahrzeug in Ordnung' },
                { value: 'leicht', label: 'Leichte Schäden — Gebrauchsspuren' },
                { value: 'mittel', label: 'Mittlere Schäden — Reparatur nötig' },
                { value: 'schwer', label: 'Schwere Schäden — Gutachten erforderlich' },
              ], group: 'Schadensprüfung' },
              { key: 'schadenscheck_kosten', label: 'Geschätzte Reparaturkosten', type: 'number', suffix: '€', group: 'Schadensprüfung' },
              { key: 'schadenscheck_notizen', label: 'Notizen', type: 'textarea', placeholder: 'Weitere Anmerkungen zum Schadenscheck...', colSpan: 2, group: 'Schadensprüfung' },
            ]}
          />
        )}

        {/* ═══════ MIETWAGEN: Abrechnung ═══════ */}
        {isMietwagenAbrechnungSection && (
          <div className="space-y-4">
            <MietwagenRechnungGenerator akte={akte} onUpdate={() => window.location.reload()} />
          </div>
        )}

        {/* ═══════ MIETWAGEN: Korrespondenz ═══════ */}
        {isMietwagenKorrespondenzSection && (
          <MietwagenKorrespondenz akte={akte} onUpdate={() => window.location.reload()} />
        )}

        {/* ═══════ GUTACHTEN: Auftrag ═══════ */}
        {isGutAuftragSection && <AuftragSection akte={akte} onChanged={refetchAkte} />}
        {/* ═══════ GUTACHTEN: Beteiligte ═══════ */}
        {isGutBeteiligteSection && <BeteiligtenTab akte={akte} onChanged={refetchAkte} />}
        {/* ═══════ GUTACHTEN: Fahrzeug ═══════ */}
        {isGutFahrzeugSection && <FahrzeugSection akte={akte} onChanged={refetchAkte} />}
        {/* ═══════ GUTACHTEN: Besichtigungen ═══════ */}
        {isGutBesichtigungenSection && <BesichtigungenSection akte={akte} />}
        {/* ═══════ GUTACHTEN: Lichtbilder ═══════ */}
        {isGutLichtbilderSection && <LichtbilderSection akte={akte} onChanged={refetchAkte} />}
        {/* ═══════ GUTACHTEN: Bewertung ═══════ */}
        {isGutBewertungSection && <BewertungSection akte={akte} />}
        {/* ═══════ GUTACHTEN: Restwert ═══════ */}
        {isGutRestwertSection && <RestwertSection akte={akte} />}
        {/* ═══════ GUTACHTEN: Minderwert ═══════ */}
        {isGutMinderwertSection && <MinderwertSection akte={akte} />}
        {/* ═══════ GUTACHTEN: Rechnung ═══════ */}
        {isGutRechnungSection && <RechnungSection akte={akte} />}
        {/* ═══════ GUTACHTEN: Protokoll ═══════ */}
        {isGutProtokollSection && <ProtokollSection akte={akte} />}
        {isGutStellungnahmeSection && <StellungnahmeTab akte={akte} onChanged={refetchAkte} />}

        {/* Andere Sektionen: Platzhalter */}
        {!isKnownSection && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">
              Dieser Bereich (<span className="font-mono font-semibold">{SECTION_LABELS[section] || section}</span>) wird noch entwickelt.
            </p>
          </div>
        )}
        </div>
      </div>

      {/* Werkstatt-Akte anlegen Modal (Issue #588) */}
      {werkstattAnlegenOpen && (
        <WerkstattAnlegenModal
          open={werkstattAnlegenOpen}
          onClose={() => setWerkstattAnlegenOpen(false)}
          gutachtenAkte={akte}
          onSuccess={refetchAkte}
        />
      )}

      {/* Link-Generator Drawer (Issue #71, erweitert in #138) */}
      <LinkGeneratorDrawer
        open={showLinkGenerator}
        onOpenChange={setShowLinkGenerator}
        akteId={akte.id}
        businessUnit={akte.business_unit}
        kennzeichen={akte.fahrzeug_kennzeichen || akte.kennzeichen || ''}
        kundeName={akte.kunde_name || ''}
        kundeTelefon={akte.anspruchsteller_telefon || akte.kunde_telefon || ''}
        kundeEmail={akte.anspruchsteller_email || akte.kunde_email || ''}
        kundeStrasse={akte.anspruchsteller_strasse || ''}
        kundePlz={akte.anspruchsteller_plz || ''}
        kundeOrt={akte.anspruchsteller_ort || ''}
        schadendatum={akte.schadendatum || akte.unfalldatum || akte.schadentag || ''}
      />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function formatEuro(val: number | null | undefined): string {
  if (val == null) return '—';
  return val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formatDatum(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE');
  } catch {
    return dateStr;
  }
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-2 sm:gap-3">
      <span className="text-[11px] sm:text-[12px] text-gray-400 w-24 sm:w-28 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-[12px] sm:text-[13px] font-medium text-gray-800 break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function TimelineItem({ label, date, status }: { label: string; date: string; status: 'fertig' | 'offen' }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status === 'fertig' ? 'bg-green-500' : 'bg-gray-300'}`} />
      <div className="flex-1 flex items-center justify-between">
        <span className="text-[12px] font-medium text-gray-700">{label}</span>
        <span className={`text-[11px] ${status === 'fertig' ? 'text-gray-500' : 'text-gray-400'}`}>{date}</span>
      </div>
    </div>
  );
}

/**
 * Stellungnahme-Timeline-Eintrag (Issue #413).
 *
 * Zeigt den aktuellen Stellungnahme-Unter-Status als Zeitverlauf-Eintrag,
 * damit der Status in der Akten-Historie sichtbar ist. Rendert nichts, wenn
 * kein Status gesetzt ist. Enthält ein Fristen-Badge, falls `schriftsatzfrist`
 * in weniger als 30 Tagen fällt.
 *
 * Ergänzt #241 (persistente, versionierte Timeline). Die Daten-seitige
 * Historie-Erzeugung folgt dort — hier stellen wir die aktuelle Momentaufnahme
 * in der bestehenden Zeitverlauf-Karte dar.
 */
function StellungnahmeTimelineItem({ akte }: { akte: Akte }) {
  const label = stellungnahmeStatusLabel(akte.stellungnahme_status);
  if (!label) return null;

  const datumQuelle = akte.updated_at || akte.created_at;
  const datumAnzeige = datumQuelle
    ? new Date(datumQuelle).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : 'Aktuell';
  const fristBadge = berechneSchriftsatzfristBadge(akte.schriftsatzfrist);

  return (
    <div className="flex items-center gap-3" data-testid="stellungnahme-timeline-item">
      <div className="w-2 h-2 rounded-full flex-shrink-0 bg-green-500" />
      <div className="flex-1 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[12px] font-medium text-gray-700">
          Stellungnahme: {label}
        </span>
        <div className="flex items-center gap-2">
          {fristBadge && fristBadge.level !== 'normal' && (
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                fristBadge.level === 'kritisch'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}
              data-testid="timeline-schriftsatzfrist-badge"
              data-schriftsatzfrist-level={fristBadge.level}
            >
              {schriftsatzfristBadgeText(fristBadge)}
            </span>
          )}
          <span className="text-[11px] text-gray-500">{datumAnzeige}</span>
        </div>
      </div>
    </div>
  );
}

// Re-export for use in AutoglasScheibeSection outside this file
export { loadPlaketteData, PLAKETTE_OPTIONS };
export type { PlaketteStatus };

/**
 * WerkstattHeaderPill (Issue #589) — kompakte Verknüpfungs-Badge im Gutachten-Akten-Header.
 * Zeigt die verknüpfte Werkstatt-Akte wenn werkstatt_akte_id gesetzt ist.
 */
function WerkstattHeaderPill({ werkstattAkteId }: { werkstattAkteId: string }) {
  const [linked, setLinked] = useState<{ id: string; akten_nummer: string | null; akten_nr: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [, navigate] = useLocation();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('akten')
          .select('id, akten_nummer, akten_nr')
          .eq('id', werkstattAkteId)
          .maybeSingle();
        if (!cancelled) setLinked(data ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [werkstattAkteId]);

  if (loading && !linked) {
    return (
      <div className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-500 text-[10px]">
        <Loader2 className="w-3 h-3 animate-spin" />
      </div>
    );
  }

  if (!linked) return null;
  const nr = aktenIdentifier(linked);
  if (!nr) return null;

  const href = buildAkteHref('werkstatt', nr);

  return (
    <a
      data-testid="werkstatt-header-pill"
      href={href}
      onClick={(e) => { e.preventDefault(); navigate(href); }}
      className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-[10px] font-semibold hover:bg-red-100 transition-colors flex-shrink-0"
    >
      <Wrench className="w-3 h-3" />
      Werkstatt-Akte {nr}
    </a>
  );
}

/**
 * GutachtenHeaderPill (Issue #615) — kompakte Verknüpfungs-Badge im Mietwagen-Akten-Header.
 * Führt Reverse-Lookup durch: findet Gutachten-Akte die auf diese Mietwagen-Akte zeigt.
 */
function GutachtenHeaderPill({ mietwagenAkteId }: { mietwagenAkteId: string }) {
  const [linked, setLinked] = useState<{ id: string; akten_nummer: string | null; akten_nr: string | null } | null>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('akten')
        .select('id, akten_nummer, akten_nr')
        .eq('mietwagen_akte_id', mietwagenAkteId)
        .eq('business_unit', 'gutachten')
        .maybeSingle();
      if (!cancelled) setLinked(data ?? null);
    })();
    return () => { cancelled = true; };
  }, [mietwagenAkteId]);

  if (!linked) return null;
  const nr = linked.akten_nummer || linked.akten_nr;
  if (!nr) return null;

  return (
    <a
      data-testid="gutachten-header-pill"
      href={`/akten/gutachten/${nr}`}
      onClick={(e) => { e.preventDefault(); navigate(`/akten/gutachten/${nr}`); }}
      className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-[10px] font-semibold hover:bg-blue-100 transition-colors flex-shrink-0"
    >
      <FileText className="w-3 h-3" />
      Gutachten {nr}
    </a>
  );
}

function MietGoChip() {
  return (
    <Badge
      data-testid="mietgo-marken-chip"
      className="hidden sm:inline-flex text-[10px] px-1.5 py-0 h-5 font-semibold flex-shrink-0 border-0"
      style={{ backgroundColor: '#0EA5E9', color: '#fff' }}
    >
      MietGo
    </Badge>
  );
}
